import React, { useState, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import DataGrid from "./DataGrid";
import GridColumnConfigurator from "./GridColumnConfigurator";
import GridFilterConfigurator from "./GridFilterConfigurator";
import GridReportExportMenu from "./GridReportExportMenu";
import GridReportExportModal from "./GridReportExportModal";
import SavedFiltersToolbar from "./SavedFiltersToolbar";
import Modal from "./Modal";
import ConfirmDialog from "./ConfirmDialog";
import Alert from "./Alert";
import PageHeader from "./PageHeader";
import Spinner from "./Spinner";
import ImageUploaderWithCrop from "../utils/ImageUploaderWithCrop";
import AdvancedLookupSelect from "./AdvancedLookupSelect";
import axBaseEnumsMetaService from "../../services/meta/axBaseEnumsMetaService";
import GridReportService from "../../services/core/GridReportService";
import UserPreferencesService from "../../services/core/UserPreferencesService";
import SavedFilterService from "../../services/core/SavedFilterService";
import { useAuth } from "../../contexts/AuthContext";

/**
 * Known error keys from backend that should be translated
 */
const KNOWN_ERROR_KEYS = {
  ERROR_COMPANY_ALREADY_LINKED: "legalEntity.errors.companyAlreadyLinked",
  error_duplicateActividadCode: "actividad.errors.duplicateCode",
  error_duplicateRecord: "common.errors.duplicateRecord",
  error_dataIntegrityViolation: "common.errors.dataIntegrityViolation",
};

/**
 * Translates a backend error key to a frontend translation
 */
const translateErrorMessage = (errorMessage, t) => {
  if (!errorMessage) {
    return errorMessage;
  }

  // Check if the error is a known key
  if (KNOWN_ERROR_KEYS[errorMessage]) {
    const translationKey = KNOWN_ERROR_KEYS[errorMessage];
    const translated = t(translationKey);
    // If translation exists and is different from key, return it
    if (translated && translated !== translationKey) {
      return translated;
    }
  }

  // Return original message if not a known key
  return errorMessage;
};

const AX_NULL_DATE_SENTINEL = "1900-01-01";

const isAxNullDateValue = (value) => {
  if (value === null || value === undefined || value === "") {
    return true;
  }

  const asString = String(value).trim();
  if (!asString) {
    return true;
  }

  return asString.startsWith(AX_NULL_DATE_SENTINEL);
};

const normalizeFieldTypeForInput = (fieldType) => {
  if (fieldType === "datetime") {
    return "date";
  }
  return fieldType;
};

const normalizeDateValueForForm = (fieldType, value) => {
  if (fieldType !== "date" && fieldType !== "datetime") {
    return value;
  }

  if (isAxNullDateValue(value)) {
    return "";
  }

  const asString = String(value).trim();
  if (fieldType === "date" || fieldType === "datetime") {
    return asString.slice(0, 10);
  }
  return asString;
};

// Identifica la cabecera a la que pertenece una fila devuelta por una consulta
// con join (una fila física por cada registro hijo, p.ej. una por línea de
// pedido). Deliberadamente ignora `__joinedRowKey` (que el backend genera
// precisamente para que cada fila física sea "única" de cara a React) porque
// aquí buscamos lo contrario: agrupar filas que comparten la misma cabecera.
const resolveJoinedRowGroupKey = (row) => {
  if (!row || typeof row !== "object") {
    return null;
  }
  const idValue = row.id;
  if (idValue !== null && idValue !== undefined) {
    return typeof idValue === "object" ? JSON.stringify(idValue) : String(idValue);
  }
  const fallbackBusinessKey = row.iSalesId ?? row.salesId ?? row.recId;
  if (fallbackBusinessKey !== null && fallbackBusinessKey !== undefined) {
    return String(fallbackBusinessKey);
  }
  return null;
};

/**
 * Colapsa filas duplicadas por un join uno-a-muchos (misma cabecera, una fila
 * por línea) en una única fila por cabecera. Para las columnas provenientes
 * del join (`column.sourceJoin`), concatena los valores distintos encontrados
 * entre las líneas del mismo pedido separados por coma; el resto de columnas
 * (campos propios de la cabecera) se toman de la primera fila del grupo, ya
 * que son idénticos en todas las filas de ese grupo.
 */
const mergeJoinedRows = (rows, columns) => {
  if (!Array.isArray(rows) || rows.length === 0) {
    return rows;
  }
  const joinedColumnKeys = (columns || [])
    .filter((column) => Boolean(column?.sourceJoin))
    .map((column) => column.key);
  if (joinedColumnKeys.length === 0) {
    return rows;
  }

  const groupOrder = [];
  const groups = new Map();

  rows.forEach((row, index) => {
    const groupKey = resolveJoinedRowGroupKey(row) ?? `__ungrouped-${index}`;
    let group = groups.get(groupKey);
    if (!group) {
      group = { baseRow: row, joinedValues: {} };
      groups.set(groupKey, group);
      groupOrder.push(groupKey);
    }
    joinedColumnKeys.forEach((key) => {
      const value = row[key];
      if (value === null || value === undefined || value === "") {
        return;
      }
      const stringValue = String(value);
      if (!group.joinedValues[key]) {
        group.joinedValues[key] = [];
      }
      if (!group.joinedValues[key].includes(stringValue)) {
        group.joinedValues[key].push(stringValue);
      }
    });
  });

  return groupOrder.map((groupKey) => {
    const group = groups.get(groupKey);
    const mergedRow = { ...group.baseRow };
    joinedColumnKeys.forEach((key) => {
      mergedRow[key] = (group.joinedValues[key] || []).join(", ");
    });
    return mergedRow;
  });
};

const getNestedValue = (source, path) => {
  if (!source || !path) return undefined;
  if (Object.prototype.hasOwnProperty.call(source, path)) {
    return source[path];
  }
  return String(path)
    .split(".")
    .reduce((value, key) => (value === null || value === undefined ? undefined : value[key]), source);
};

const resolveExportOptionLabel = (column, value) => {
  const options = resolveSelectOptions(column, {});
  const option = options.find((candidate) => String(candidate.value) === String(value));
  return option?.label ?? value;
};

const normalizeExportValue = (row, column, t) => {
  let value;
  if (typeof column.exportValue === "function") {
    value = column.exportValue(row);
  } else {
    value = getNestedValue(row, column.exportKey || column.key);
  }

  const fieldType = column.type ?? column.formConfig?.type;
  if (fieldType === "select") {
    value = resolveExportOptionLabel(column, value);
  }
  if (typeof value === "boolean") {
    return t(value ? "common.yes" : "common.no");
  }
  if (value === null || value === undefined) {
    return "";
  }
  if (Array.isArray(value)) {
    return value.map((item) => (
      item && typeof item === "object"
        ? item.label ?? item.description ?? item.name ?? JSON.stringify(item)
        : item
    )).join(", ");
  }
  if (typeof value === "object") {
    return String(value.label ?? value.description ?? value.name ?? value.id ?? JSON.stringify(value));
  }
  return String(value);
};

const normalizeDateValueForSubmit = (fieldType, value) => {
  if (fieldType !== "date" && fieldType !== "datetime") {
    return value;
  }

  if (value === "" || isAxNullDateValue(value)) {
    return null;
  }

  const asString = String(value).trim();
  if (/^\d{4}-\d{2}-\d{2}$/.test(asString)) {
    return `${asString}T00:00:00`;
  }

  return value;
};

const normalizeDateOnlyPayloadFields = (payload, columns) => {
  if (!payload || typeof payload !== "object" || !Array.isArray(columns)) {
    return payload;
  }

  for (const col of columns) {
    if (!col) {
      continue;
    }
    const fieldType = col.type ?? col.formConfig?.type;
    if (fieldType !== "date" && fieldType !== "datetime") {
      continue;
    }

    const candidateKeys = [col.key, col.backendKey].filter(
      (key, index, arr) => typeof key === "string" && key && arr.indexOf(key) === index,
    );

    for (const key of candidateKeys) {
      if (!Object.prototype.hasOwnProperty.call(payload, key)) {
        continue;
      }
      const rawValue = payload[key];
      if (rawValue === null || rawValue === undefined || rawValue === "") {
        continue;
      }
      const asString = String(rawValue).trim();
      if (/^\d{4}-\d{2}-\d{2}$/.test(asString)) {
        payload[key] = `${asString}T00:00:00`;
      }
    }
  }

  return payload;
};
const SELECT_LABEL_CANDIDATES = [
  "name",
  "txt",
  "description",
  "descripcion",
  "nome",
  "label",
  "title",
];

const SELECT_VALUE_CANDIDATES = ["id", "code", "key", "recId", "recid"];

const resolvePreferredOptionField = (sample, explicitField, candidates) => {
  if (!sample || typeof sample !== "object") {
    return null;
  }

  if (
    explicitField &&
    Object.prototype.hasOwnProperty.call(sample, explicitField)
  ) {
    return explicitField;
  }

  for (const candidate of candidates) {
    if (Object.prototype.hasOwnProperty.call(sample, candidate)) {
      return candidate;
    }
  }

  const keys = Object.keys(sample);
  for (const key of keys) {
    if (key && key.toLowerCase().endsWith("id")) {
      return key;
    }
  }

  return null;
};

const resolveSelectOptions = (col, dynamicOptions = {}) =>
  dynamicOptions[col.key] ?? col.options ?? col.formConfig?.options ?? [];

const normalizeLookupFieldList = (value) => {
  if (!Array.isArray(value)) {
    return [];
  }
  return value
    .filter((field) => typeof field === "string" && field.trim() !== "")
    .map((field) => field.trim());
};

const resolveLookupSort = (col) => {
  const rawSort = col.lookupSort ?? col.formConfig?.lookupSort;
  if (!rawSort || typeof rawSort !== "object") {
    return null;
  }
  const field = rawSort.field;
  if (!field || typeof field !== "string") {
    return null;
  }
  const direction =
    String(rawSort.direction || "asc").toLowerCase() === "desc"
      ? "desc"
      : "asc";
  return { field, direction };
};

const buildLookupSortArray = (col) => {
  const sort = resolveLookupSort(col);
  if (!sort) {
    return [];
  }
  return [{ field: sort.field, direction: sort.direction }];
};

const hasAdvancedLookupConfig = (col) => {
  const displayFields = normalizeLookupFieldList(
    col.lookupDisplayFields ?? col.formConfig?.lookupDisplayFields,
  );
  const searchFields = normalizeLookupFieldList(
    col.lookupSearchFields ?? col.formConfig?.lookupSearchFields,
  );
  return displayFields.length > 1 || searchFields.length > 0;
};

const buildLookupOptionLabel = (
  item,
  optionLabel,
  lookupDisplayFields = [],
  lookupSelectedLabelField = null,
) => {
  const safeItem = item || {};
  const selectedLabelField =
    typeof lookupSelectedLabelField === "string" &&
    lookupSelectedLabelField.trim() !== ""
      ? lookupSelectedLabelField.trim()
      : null;

  if (selectedLabelField) {
    const selectedValue = safeItem?.[selectedLabelField];
    if (selectedValue !== undefined && selectedValue !== null) {
      return String(selectedValue);
    }
  }

  const visibleFields = normalizeLookupFieldList(lookupDisplayFields);

  if (visibleFields.length > 0) {
    const values = visibleFields
      .map((field) => safeItem[field])
      .filter((value) => value !== undefined && value !== null && String(value).trim() !== "")
      .map((value) => String(value));
    if (values.length > 0) {
      return values.join(" | ");
    }
  }

  const fallbackValue = safeItem?.[optionLabel];
  if (fallbackValue !== undefined && fallbackValue !== null) {
    return String(fallbackValue);
  }
  return "";
};

const normalizeOptionValue = (value) => {
  if (value === null || value === undefined) {
    return "";
  }
  return String(value);
};

const isUnresolvedAxLabel = (label) => {
  if (label === null || label === undefined) {
    return true;
  }
  if (typeof label !== "string") {
    return false;
  }
  const trimmed = label.trim();
  return trimmed === "" || trimmed.startsWith("@");
};

const isUsableSymbolOrName = (symbolOrName) => {
  if (typeof symbolOrName !== "string") {
    return false;
  }
  const trimmed = symbolOrName.trim();
  return trimmed !== "" && !trimmed.startsWith("@");
};

const mergeRuntimeEnumOptions = (runtimeOptions = [], fallbackOptions = []) => {
  const safeRuntime = Array.isArray(runtimeOptions) ? runtimeOptions : [];
  const safeFallback = Array.isArray(fallbackOptions) ? fallbackOptions : [];

  if (safeRuntime.length === 0) {
    return safeFallback;
  }

  const fallbackByValue = new Map();
  safeFallback.forEach((option) => {
    const key = normalizeOptionValue(option?.value);
    if (!key || fallbackByValue.has(key)) {
      return;
    }
    fallbackByValue.set(key, option);
  });

  const merged = [];
  const seenValues = new Set();

  safeRuntime.forEach((option) => {
    const key = normalizeOptionValue(option?.value);
    if (!key || seenValues.has(key)) {
      return;
    }
    seenValues.add(key);

    const runtimeLabel = option?.label;
    const fallbackOption = fallbackByValue.get(key);
    const fallbackLabel = fallbackOption?.label;
    const symbolOrName = option?.symbolOrName;

    let labelToUse = runtimeLabel;
    if (isUnresolvedAxLabel(runtimeLabel)) {
      if (!isUnresolvedAxLabel(fallbackLabel)) {
        labelToUse = fallbackLabel;
      } else if (isUsableSymbolOrName(symbolOrName)) {
        labelToUse = symbolOrName;
      }
    }

    merged.push({
      ...option,
      value: option?.value,
      label: labelToUse,
    });
  });

  safeFallback.forEach((option) => {
    const key = normalizeOptionValue(option?.value);
    if (!key || seenValues.has(key)) {
      return;
    }
    seenValues.add(key);
    merged.push(option);
  });

  return merged;
};

const normalizeLanguageCode = (value) => {
  if (!value || typeof value !== "string") {
    return "";
  }
  return value.toLowerCase().split("-")[0];
};

const buildColumnsWithResolvedOptions = (columns, dynamicOptions = {}) =>
  columns.map((col) => {
    const options = dynamicOptions[col.key];
    if (!Array.isArray(options) || options.length === 0) {
      return col;
    }
    return {
      ...col,
      options,
    };
  });

const translateOptionLabels = (options, t) => {
  if (!Array.isArray(options)) {
    return options;
  }
  return options.map((option) => {
    if (!option || typeof option !== "object") {
      return option;
    }
    const translationKey = option.labelKey || option.label;
    if (typeof translationKey !== "string") {
      return option;
    }
    return {
      ...option,
      label: t(translationKey, { defaultValue: option.label ?? translationKey }),
    };
  });
};

const getLookupDependsOn = (col) =>
  col.lookupDependsOn ?? col.formConfig?.lookupDependsOn ?? [];

const buildSelectDependencyMap = (columns) => {
  const dependentsMap = {};

  columns.forEach((col) => {
    const deps = getLookupDependsOn(col);
    if (!Array.isArray(deps)) return;

    deps.forEach((dep) => {
      const parent = dep?.localField;
      if (!parent) return;
      if (!dependentsMap[parent]) {
        dependentsMap[parent] = [];
      }
      if (!dependentsMap[parent].includes(col.key)) {
        dependentsMap[parent].push(col.key);
      }
    });
  });

  return dependentsMap;
};

const collectDependentFields = (dependentsMap, parentField) => {
  const visited = new Set();
  const queue = [parentField];

  while (queue.length > 0) {
    const current = queue.shift();
    const directChildren = dependentsMap[current] || [];
    directChildren.forEach((child) => {
      if (visited.has(child)) return;
      visited.add(child);
      queue.push(child);
    });
  }

  return visited;
};

const buildLookupFilters = (col, values) => {
  const fixedFilters = col.lookupFixedFilters ?? col.formConfig?.lookupFixedFilters ?? {};
  const deps = getLookupDependsOn(col);
  if (!Array.isArray(deps) || deps.length === 0) {
    return { canLoad: true, filters: { ...fixedFilters } };
  }

  const filters = { ...fixedFilters };
  for (const dep of deps) {
    const parentField = dep?.localField;
    const remoteFilterField = dep?.remoteFilterField;
    if (!parentField || !remoteFilterField) {
      continue;
    }

    const parentValue = values?.[parentField];
    if (parentValue === undefined || parentValue === null || parentValue === "") {
      return { canLoad: false, filters: {} };
    }

    filters[remoteFilterField] = parentValue;
  }

  return { canLoad: true, filters };
};

const hasMissingLookupDependencies = (col, values) =>
  !buildLookupFilters(col, values).canLoad;

/**
 * Genera initialFormData automáticamente desde columns
 */
const generateInitialFormData = (columns) => {
  const formData = {};
  columns.forEach((col) => {
    // Soportar tanto la estructura nueva (aplanada) como la antigua (anidada)
    const showInForm = col.showInForm ?? col.formConfig?.show;
    if (showInForm) {
      formData[col.key] = "";
    }
  });
  return formData;
};

/**
 * Convierte base64 a Blob para envío al backend
 */
const base64ToBlob = async (base64) => {
  try {
    const response = await fetch(base64);
    return await response.blob();
  } catch (error) {
    console.error("Error converting base64 to blob:", error);
    throw error;
  }
};

/**
 * Detecta si hay campos de tipo 'image' en columns
 */
const hasImageFields = (columns) => {
  return columns.some((col) => {
    const fieldType = col.type ?? col.formConfig?.type;
    return fieldType === "image";
  });
};

/**
 * Construye FormData automáticamente si hay campos de imagen
 * Si no hay imágenes, devuelve el objeto con las claves transformadas
 */
const buildFormData = async (formData, columns, isUpdate = false) => {
  // Si no hay campos de imagen, transformar el objeto y retornarlo
  if (!hasImageFields(columns)) {
    const transformedData = { ...formData };

    for (const col of columns) {
      const showInForm = col.showInForm ?? col.formConfig?.show;
      if (!showInForm) continue;

      const fieldType = col.type ?? col.formConfig?.type;
      const fieldValue = normalizeDateValueForSubmit(
        fieldType,
        formData[col.key],
      );

      // Si hay un backendKey diferente o es un select con relación JPA, transformar la clave
      const backendKey = col.backendKey ?? col.key;

      // Para selects con backendKey explícito (relaciones JPA), crear objeto anidado
      // Se detecta porque hay un backendKey explícito (diferente del key por defecto)
      const hasExplicitBackendKey = col.backendKey !== undefined;

      if (fieldType === "select" && hasExplicitBackendKey) {
        // Para selects, siempre enviar como campo plano con el valor directo
        // Esto evita errores de deserialización JSON en el backend
        if (
          fieldValue !== undefined &&
          fieldValue !== null &&
          fieldValue !== ""
        ) {
          transformedData[backendKey] = fieldValue;
          if (backendKey !== col.key) {
            delete transformedData[col.key];
          }
        }
      }
    }

    normalizeDateOnlyPayloadFields(transformedData, columns);
    return transformedData;
  }

  // Construir FormData
  const data = new FormData();

  for (const col of columns) {
    const showInForm = col.showInForm ?? col.formConfig?.show;
    if (!showInForm) continue;

    const fieldType = col.type ?? col.formConfig?.type;
    const fieldValue = normalizeDateValueForSubmit(
      fieldType,
      formData[col.key],
    );

    // Nombre del campo en el backend (puede ser diferente del key)
    const backendKey = col.backendKey ?? col.key;

    if (fieldType === "image") {
      // Procesar imagen
      const shouldProcessImage = isUpdate
        ? fieldValue &&
          typeof fieldValue === "string" &&
          fieldValue.startsWith("data:image/")
        : fieldValue;

      if (shouldProcessImage) {
        const imageBlob = await base64ToBlob(fieldValue);
        data.append(backendKey, imageBlob, `${backendKey}.png`);
      }
    } else if (fieldType === "select") {
      // Para selects, el backend puede esperar un nombre diferente (ej: categoryId)
      const selectBackendKey = col.backendKey ?? `${col.key}Id`;
      if (
        fieldValue !== undefined &&
        fieldValue !== null &&
        fieldValue !== ""
      ) {
        data.append(selectBackendKey, fieldValue);
      }
    } else {
      // Para campos regulares, añadir el valor
      if (fieldValue !== undefined && fieldValue !== null) {
        data.append(backendKey, fieldValue);
      }
    }
  }

  // Añadir 'enabled' si no está en columns (campo implícito)
  if (formData.enabled !== undefined) {
    data.append("enabled", formData.enabled || false);
  }

  return data;
};

/**
 * Calcula automáticamente el ancho de columna según el tamaño relativo y total de filtros
 */
const calculateFilterWidth = (totalFilters, filterSize) => {
  // Mapeo de tamaños relativos a proporciones
  const sizeMap = {
    small: 2,
    medium: 4,
    large: 6,
  };

  // Si se especifica un tamaño relativo
  if (filterSize && sizeMap[filterSize]) {
    return `col-md-${sizeMap[filterSize]}`;
  }

  // Distribución automática equitativa
  if (totalFilters === 1) return "col-md-12";
  if (totalFilters === 2) return "col-md-6";
  if (totalFilters === 3) return "col-md-4";
  if (totalFilters === 4) return "col-md-3";
  if (totalFilters === 5) return "col-md-2";

  // Para 6 o más, usar 2 columnas (col-md-2)
  return "col-md-2";
};

const inferJoinTableFromField = (field) => {
  const candidates = [
    field?.sourceJoin,
    field?.sourceTable,
    field?.table,
    field?.backendKey,
    field?.filterKey,
    field?.filterConfig?.filterKey,
    field?.sortKey,
    field?.key,
  ];

  for (const candidate of candidates) {
    const value = String(candidate || "").trim();
    if (value.includes(".")) {
      return value.split(".")[0];
    }
  }

  return null;
};

/**
 * Renderiza automáticamente filtros desde la configuración de columns
 */
const autoRenderFilters = (
  columns,
  filterStates,
  onFilterChange,
  t,
  dynamicOptions = {},
) => {
  // Soportar tanto estructura nueva (aplanada) como antigua (anidada)
  const filterFields = columns.filter(
    (col) => col.showInFilter ?? col.filterConfig?.show,
  );

  if (filterFields.length === 0) return null;

  const totalFilters = filterFields.length;

  return (
    <div className="filter-form row">
      {filterFields.map((col) => {
        // Soportar ambas estructuras
        const filterKey =
          col.filterKey ?? col.filterConfig?.filterKey ?? col.key;
        const filterSize = col.filterSize ?? col.filterConfig?.size;
        const filterPlaceholder =
          col.filterPlaceholder ??
          col.filterConfig?.placeholder ??
          col.placeholder;
        const filterLabel =
          col.filterLabel ?? col.filterConfig?.label ?? col.label;

        // ðŸŽ‰ Traducir automáticamente si es un string de traducción
        const translatedFilterPlaceholder =
          typeof filterPlaceholder === "string" &&
          filterPlaceholder.includes(".")
            ? t(filterPlaceholder)
            : filterPlaceholder;
        const translatedFilterLabel =
          typeof filterLabel === "string" && filterLabel.includes(".")
            ? t(filterLabel)
            : filterLabel;

        // ðŸŽ‰ Cálculo automático del ancho según cantidad de filtros
        const colWidth = calculateFilterWidth(totalFilters, filterSize);

        // ðŸŽ‰ Heredar tipo de nivel raíz si no se especifica
        const explicitFilterType = col.filterType ?? col.filterConfig?.type;
        const hasRemoteLookup = Boolean(
          col.relatedService ?? col.formConfig?.relatedService,
        );
        const inheritedFilterType = col.type ?? "text";
        const filterType =
          hasRemoteLookup && !explicitFilterType && inheritedFilterType === "select"
            ? "text"
            : explicitFilterType ?? inheritedFilterType;
        // Para filtros, convertir tipos complejos a simples
        const normalizedType =
          filterType === "textarea"
            ? "text"
            : filterType === "number"
              ? "text"
              : filterType === "image"
                ? null
                : filterType;

        // No renderizar filtros para tipos que no tienen sentido (image, password, etc)
        if (!normalizedType || normalizedType === "password") return null;

        return (
          <div key={col.key} className={colWidth}>
            {normalizedType === "checkbox" ? (
              <>
                <label htmlFor={`filter-${col.key}`} className="filter-field-label">
                  {translatedFilterLabel}
                </label>
                <input
                  id={`filter-${col.key}`}
                  type="checkbox"
                  checked={filterStates[col.key] || false}
                  onChange={(e) =>
                    onFilterChange(col.key, filterKey, e.target.checked)
                  }
                />
              </>
            ) : normalizedType === "select" ? (
              (() => {
                const filterOptions = resolveSelectOptions(col, dynamicOptions);
                const filterHasMissingDependencies = hasMissingLookupDependencies(
                  col,
                  filterStates,
                );
                return (
                  <>
                    <label htmlFor={`filter-${col.key}`} className="filter-field-label">
                      {translatedFilterLabel}
                    </label>
                    <select
                      id={`filter-${col.key}`}
                      name={col.key}
                      className="form-control"
                      value={filterStates[col.key] ?? ""}
                      onChange={(e) =>
                        onFilterChange(col.key, filterKey, e.target.value)
                      }
                      disabled={filterHasMissingDependencies}
                      title={
                        filterOptions.find(
                          (opt) =>
                            String(opt?.value) === String(filterStates[col.key]),
                        )?.label || ""
                      }
                    >
                      <option value="">
                        {translatedFilterPlaceholder || t("common.all")}
                      </option>
                      {filterOptions.map((option) => (
                        <option
                          key={option.value}
                          value={option.value}
                          title={option.label}
                        >
                          {option.label}
                        </option>
                      ))}
                    </select>
                  </>
                );
              })()
            ) : (
              <>
                <label
                  htmlFor={`filter-${col.key}`}
                  className="filter-field-label filter-field-label-spacer"
                >
                  {translatedFilterLabel}
                </label>
                <div className="filter-input-wrapper">
                  <input
                    id={`filter-${col.key}`}
                    type="text"
                    name={col.key}
                    className="form-control"
                    placeholder={
                      normalizedType === "date"
                        ? "AAAA-MM-DD o (Today-30)..(Today)"
                        : translatedFilterPlaceholder || translatedFilterLabel
                    }
                    title={
                      normalizedType === "date"
                        ? "Fecha exacta (AAAA-MM-DD) o expresión relativa: (Today), (Today-30), (Today-30)..(Today)"
                        : undefined
                    }
                    value={filterStates[col.key] || ""}
                    onChange={(e) =>
                      onFilterChange(col.key, filterKey, e.target.value)
                    }
                  />
                  {filterStates[col.key] && (
                    <button
                      type="button"
                      className="filter-clear-button"
                      onClick={() => onFilterChange(col.key, filterKey, "")}
                      aria-label="Clear filter"
                    >
                      <i className="fa fa-times"></i>
                    </button>
                  )}
                </div>
              </>
            )}
          </div>
        );
      })}
    </div>
  );
};

/**
 * Valida automáticamente los campos required del formulario
 * Retorna objeto con errores por campo: { fieldKey: 'mensaje de error' }
 */
const autoValidateForm = (formData, columns, t, entityName) => {
  const errors = {};

  for (const col of columns) {
    // Soportar tanto estructura nueva (aplanada) como antigua (anidada)
    const showInForm = col.showInForm ?? col.formConfig?.show;
    const required = col.required ?? col.formConfig?.required;

    if (showInForm && required) {
      const value = formData[col.key];
      if (!value || (typeof value === "string" && value.trim() === "")) {
        // ðŸŽ‰ Traducir label si es una clave de traducción
        const translatedLabel =
          typeof col.label === "string" && col.label.includes(".")
            ? t(col.label)
            : col.label;

        // Intentar traducción específica, si no existe usar genérica
        const errorKey = `${entityName}.errors.${col.key}Required`;
        const genericError = `${translatedLabel} ${t("validation.isRequired") || "es requerido"}`;
        errors[col.key] = t(errorKey, { defaultValue: genericError });
      }
    }
  }

  return Object.keys(errors).length > 0 ? errors : null;
};

const humanizeTechnicalLayoutId = (value, fallback = "") => {
  const raw = (value || "").toString().trim();
  if (!raw) {
    return fallback || "";
  }
  const normalized = raw
    .replace(/[_-]+/g, " ")
    .replace(/([a-z0-9])([A-Z])/g, "$1 $2")
    .replace(/\s+/g, " ")
    .trim();
  if (!normalized) {
    return fallback || raw;
  }
  return normalized.charAt(0).toUpperCase() + normalized.slice(1);
};

const resolveLayoutLabel = (labelValue, t, fallback = "") => {
  if (typeof labelValue === "string") {
    const trimmed = labelValue.trim();
    if (trimmed) {
      if (trimmed.includes(".")) {
        const translated = t(trimmed);
        if (translated && translated !== trimmed) {
          return translated;
        }
      } else {
        return trimmed;
      }
    }
  }
  return humanizeTechnicalLayoutId(fallback, fallback || "");
};

const normalizeTabIconClass = (iconValue) => {
  if (typeof iconValue !== "string") {
    return "";
  }

  const raw = iconValue.trim();
  if (!raw) {
    return "";
  }

  if (raw.includes(" ")) {
    return raw;
  }

  if (raw.startsWith("fa-")) {
    return `fa ${raw}`;
  }

  if (raw.startsWith("fa")) {
    const normalized = raw
      .replace(/^fa/, "")
      .replace(/([a-z0-9])([A-Z])/g, "$1-$2")
      .replace(/^-+/, "")
      .toLowerCase();
    return normalized ? `fa fa-${normalized}` : "fa";
  }

  return `fa fa-${raw.toLowerCase()}`;
};

const inferTabIconClass = (tabId, tabLabel) => {
  const probe = `${tabId || ""} ${tabLabel || ""}`.toLowerCase();
  if (!probe.trim()) {
    return "";
  }
  if (probe.includes("overview") || probe.includes("general")) return "fa fa-list-alt";
  if (probe.includes("setup") || probe.includes("config")) return "fa fa-cog";
  if (probe.includes("address")) return "fa fa-map-marker";
  if (probe.includes("delivery")) return "fa fa-truck";
  if (probe.includes("price") || probe.includes("calc") || probe.includes("discount")) return "fa fa-percent";
  if (probe.includes("payment")) return "fa fa-credit-card";
  if (probe.includes("dimension")) return "fa fa-cubes";
  if (probe.includes("communication") || probe.includes("contact")) return "fa fa-address-book";
  if (probe.includes("other") || probe.includes("various")) return "fa fa-ellipsis-h";
  return "";
};

const normalizeFormTabs = (formTabs, columns, t) => {
  if (!Array.isArray(formTabs) || formTabs.length === 0) {
    return [];
  }

  const availableFormFieldKeys = new Set(
    columns
      .filter((col) => col.showInForm ?? col.formConfig?.show)
      .map((col) => col.key),
  );

  const globalSeen = new Set();

  const normalizeFieldList = (fields) => {
    if (!Array.isArray(fields) || fields.length === 0) {
      return [];
    }

    const seen = new Set();
    return fields.filter((fieldKey) => {
      if (typeof fieldKey !== "string") {
        return false;
      }
      if (!availableFormFieldKeys.has(fieldKey)) {
        return false;
      }
      if (seen.has(fieldKey)) {
        return false;
      }
      if (globalSeen.has(fieldKey)) {
        return false;
      }
      seen.add(fieldKey);
      globalSeen.add(fieldKey);
      return true;
    });
  };

  return formTabs
    .map((tab, tabIndex) => {
      const tabId =
        typeof tab?.tabId === "string" && tab.tabId.trim()
          ? tab.tabId
          : `ax-tab-${tabIndex + 1}`;
      const tabLabel = resolveLayoutLabel(tab?.label, t, tabId);

      const groups = Array.isArray(tab?.groups)
        ? tab.groups
            .map((group, groupIndex) => {
              const groupId =
                typeof group?.groupId === "string" && group.groupId.trim()
                  ? group.groupId
                  : `${tabId}-group-${groupIndex + 1}`;
              const groupLabel = resolveLayoutLabel(group?.label, t, groupId);
              const fields = normalizeFieldList(group?.fields);
              if (!fields.length) {
                return null;
              }
              return { groupId, label: groupLabel, fields };
            })
            .filter(Boolean)
        : [];

      const tabFields = normalizeFieldList(tab?.fields);
      if (!groups.length && !tabFields.length) {
        return null;
      }

      return {
        tabId,
        label: tabLabel,
        iconClass:
          normalizeTabIconClass(tab?.icon) || inferTabIconClass(tabId, tab?.label || tabLabel),
        groups,
        fields: tabFields,
      };
    })
    .filter(Boolean);
};

const getFormContainerClass = (totalFields) => {
  if (totalFields <= 1) {
    return "";
  }
  if (totalFields <= 6) {
    return "form-row-2cols";
  }
  return "form-row-3cols";
};

/**
 * Renderiza automáticamente un formulario desde la configuración de columns
 */
const autoRenderForm = (
  columns,
  formData,
  onChange,
  isEditing,
  t,
  isEditImageState,
  setEditImageState,
  fieldErrors = {},
  propsDynamicOptions = {},
  formTabs = [],
  activeFormTabId = null,
  onTabChange = null,
) => {
  const formFields = columns.filter(
    (col) => col.showInForm ?? col.formConfig?.show,
  );
  const formFieldsByKey = {};
  formFields.forEach((col) => {
    formFieldsByKey[col.key] = col;
  });

  const renderField = (col, totalFields) => {
    const fieldId = `field-${col.key}`;

    const fieldType = col.type ?? col.formConfig?.type ?? "text";
    const normalizedFieldType = normalizeFieldTypeForInput(fieldType);
    const fieldPlaceholder = col.placeholder ?? col.formConfig?.placeholder;
    const fieldRequired = col.required ?? col.formConfig?.required;
    const fieldIcon = col.icon ?? col.formConfig?.icon;
    const fieldFullWidth = col.fullWidth ?? col.formConfig?.fullWidth;
    const fieldDisabled = col.disabled ?? col.formConfig?.disabled;
    const fieldRows = col.rows ?? col.formConfig?.rows;
    const fieldStep = col.step ?? col.formConfig?.step;
    const fieldMin = col.min ?? col.formConfig?.min;
    const fieldOptions = resolveSelectOptions(col, propsDynamicOptions);
    const relatedService = col.relatedService ?? col.formConfig?.relatedService;
    const optionLabel = col.optionLabel ?? col.formConfig?.optionLabel;
    const optionValue = col.optionValue ?? col.formConfig?.optionValue;
    const lookupDisplayFields = normalizeLookupFieldList(
      col.lookupDisplayFields ?? col.formConfig?.lookupDisplayFields,
    );
    const lookupSearchFields = normalizeLookupFieldList(
      col.lookupSearchFields ?? col.formConfig?.lookupSearchFields,
    );
    const lookupOptionValueFields = normalizeLookupFieldList(
      col.lookupOptionValueFields ?? col.formConfig?.lookupOptionValueFields,
    );
    const lookupAllowFreeText = Boolean(
      col.lookupAllowFreeText ?? col.formConfig?.lookupAllowFreeText,
    );
    const lookupSort = resolveLookupSort(col);
    const lookupSearchMode =
      col.lookupSearchMode ?? col.formConfig?.lookupSearchMode;
    const lookupSelectedLabelField =
      col.lookupSelectedLabelField ?? col.formConfig?.lookupSelectedLabelField;
    const useAdvancedLookup =
      fieldType === "select" &&
      !!relatedService &&
      hasAdvancedLookupConfig(col);
    const fieldChangeButtonText =
      col.changeButtonText ?? col.formConfig?.changeButtonText;

    const isDisabled =
      typeof fieldDisabled === "function"
        ? fieldDisabled(isEditing, formData)
        : fieldDisabled;
    const lookupFilterResult = buildLookupFilters(col, formData);
    const hasMissingDependencies = !lookupFilterResult.canLoad;

    const fullWidthClass =
      totalFields > 1 && fieldFullWidth ? "form-full-width" : "";

    const translatedLabel =
      typeof col.label === "string" && col.label.includes(".")
        ? t(col.label)
        : col.label;
    const translatedPlaceholder =
      typeof fieldPlaceholder === "string" && fieldPlaceholder.includes(".")
        ? t(fieldPlaceholder)
        : fieldPlaceholder;
    const translatedChangeButtonText =
      typeof fieldChangeButtonText === "string" &&
      fieldChangeButtonText.includes(".")
        ? t(fieldChangeButtonText)
        : fieldChangeButtonText;

    const fieldClassName = col.className ?? col.formConfig?.className ?? "";

    return (
      <div key={col.key} className={`form-group ${fullWidthClass} ${fieldClassName}`.trim()}>
        <label htmlFor={fieldId}>
          {fieldIcon && <i className={`fa ${fieldIcon}`}></i>} {translatedLabel}
          {fieldRequired && (
            <span className="required-field" aria-label="required">
              *
            </span>
          )}
        </label>

        {fieldType === "image" ? (
          <>
            {isEditing &&
            formData[col.key] &&
            typeof formData[col.key] === "string" &&
            formData[col.key].startsWith("http") &&
            !isEditImageState ? (
              <div style={{ textAlign: "center" }}>
                <img
                  src={`${formData[col.key]}?v=${new Date().getTime()}`}
                  alt=""
                  className="preview-image-modal"
                  style={{ marginBottom: "12px" }}
                />
                <br />
                <button
                  type="button"
                  onClick={() => setEditImageState(true)}
                  className="btn btn-secondary"
                  style={{ marginTop: "8px" }}
                >
                  <i className="fa fa-edit"></i>{" "}
                  {translatedChangeButtonText || t("common.changeImage")}
                </button>
              </div>
            ) : (
              <ImageUploaderWithCrop
                onImageChange={(croppedImage) =>
                  onChange({ ...formData, [col.key]: croppedImage })
                }
              />
            )}
          </>
        ) : fieldType === "textarea" ? (
          <textarea
            id={fieldId}
            name={col.key}
            className={`form-control ${fieldErrors[col.key] ? "has-error" : ""}`}
            placeholder={translatedPlaceholder || translatedLabel}
            value={formData[col.key] || ""}
            onChange={(e) => onChange({ ...formData, [col.key]: e.target.value })}
            rows={fieldRows || 3}
            disabled={isDisabled}
            required={fieldRequired}
            aria-required={fieldRequired}
            aria-invalid={!!fieldErrors[col.key]}
          />
        ) : fieldType === "select" && useAdvancedLookup ? (
          <AdvancedLookupSelect
            id={fieldId}
            name={col.key}
            value={formData[col.key] ?? ""}
            onValueChange={(nextValue) =>
              onChange({ ...formData, [col.key]: nextValue })
            }
            disabled={isDisabled || hasMissingDependencies}
            required={fieldRequired}
            ariaInvalid={!!fieldErrors[col.key]}
            placeholder={
              translatedPlaceholder || `${t("common.search")} ${translatedLabel}`
            }
            dependencyFilters={lookupFilterResult.filters}
            staticOptions={propsDynamicOptions[col.key] || fieldOptions}
            allowFreeText={lookupAllowFreeText}
            lookupConfig={{
              relatedService,
              optionLabel,
              optionValue,
              optionValueFields: lookupOptionValueFields,
              displayFields: lookupDisplayFields,
              displayFieldLabels:
                col.lookupDisplayFieldLabels ??
                col.formConfig?.lookupDisplayFieldLabels,
              searchFields: lookupSearchFields,
              searchMode: lookupSearchMode,
              sort: lookupSort,
              selectedLabelField: lookupSelectedLabelField,
              enumFields: col.lookupEnumFields ?? col.formConfig?.lookupEnumFields,
              valueLabels: col.lookupValueLabels ?? col.formConfig?.lookupValueLabels,
              columnMaxWidth:
                col.lookupColumnMaxWidth ?? col.formConfig?.lookupColumnMaxWidth,
              columnMaxWidthByField:
                col.lookupColumnMaxWidthByField ??
                col.formConfig?.lookupColumnMaxWidthByField,
            }}
          />
        ) : fieldType === "select" ? (
          <select
            id={fieldId}
            name={col.key}
            className={`form-control ${fieldErrors[col.key] ? "has-error" : ""}`}
            value={formData[col.key] ?? ""}
            onChange={(e) => onChange({ ...formData, [col.key]: e.target.value })}
            disabled={isDisabled || hasMissingDependencies}
            required={fieldRequired}
            aria-required={fieldRequired}
            aria-invalid={!!fieldErrors[col.key]}
            title={
              (propsDynamicOptions[col.key] || fieldOptions)?.find(
                (opt) => String(opt?.value) === String(formData[col.key]),
              )?.label || ""
            }
          >
            <option value="">
              {translatedPlaceholder || `${t("common.select")} ${translatedLabel}`}
            </option>
            {(propsDynamicOptions[col.key] || fieldOptions)?.map((option) => (
              <option key={option.value} value={option.value} title={option.label}>
                {option.label}
              </option>
            ))}
          </select>
        ) : fieldType === "checkbox" ? (
          <label className="crud-checkbox-field" htmlFor={fieldId}>
            <input
              id={fieldId}
              name={col.key}
              type="checkbox"
              checked={formData[col.key] === true}
              onChange={(e) => onChange({ ...formData, [col.key]: e.target.checked })}
              disabled={isDisabled}
              aria-invalid={!!fieldErrors[col.key]}
            />
            <span className="crud-checkbox-box" aria-hidden="true">
              <i className="fa fa-check"></i>
            </span>
          </label>
        ) : (
          <input
            id={fieldId}
            name={col.key}
            type={normalizedFieldType || "text"}
            className={`form-control ${fieldErrors[col.key] ? "has-error" : ""}`}
            placeholder={translatedPlaceholder || translatedLabel}
            value={formData[col.key] || ""}
            onChange={(e) => onChange({ ...formData, [col.key]: e.target.value })}
            disabled={isDisabled}
            required={fieldRequired}
            aria-required={fieldRequired}
            aria-invalid={!!fieldErrors[col.key]}
            {...(fieldType === "number" && {
              step: fieldStep || "0.01",
              min: fieldMin || "0",
            })}
          />
        )}

        {fieldErrors[col.key] && (
          <div className="field-error-message">{fieldErrors[col.key]}</div>
        )}
      </div>
    );
  };

  const renderFieldsGrid = (fieldKeys) => {
    const mappedFields = (fieldKeys || [])
      .map((key) => formFieldsByKey[key])
      .filter(Boolean);
    if (!mappedFields.length) {
      return null;
    }

    const containerClass = getFormContainerClass(mappedFields.length);
    return (
      <div className={containerClass}>
        {mappedFields.map((col) => renderField(col, mappedFields.length))}
      </div>
    );
  };

  const usableTabs = normalizeFormTabs(formTabs, columns, t);
  if (!usableTabs.length) {
    return renderFieldsGrid(formFields.map((col) => col.key));
  }

  const currentTabId = activeFormTabId || usableTabs[0].tabId;
  const currentTab =
    usableTabs.find((tab) => tab.tabId === currentTabId) || usableTabs[0];
  const showTabsNav = usableTabs.length > 1;

  return (
    <div className="crud-form-tabs">
      {showTabsNav ? (
        <div className="crud-form-tabs-nav-wrap" aria-label="Form sections">
          <div className="crud-form-tabs-nav-scroll" role="tablist">
            <ul className="nav nav-tabs crud-form-tabs-nav">
              {usableTabs.map((tab) => {
                const isActive = tab.tabId === currentTab.tabId;
                return (
                  <li key={tab.tabId} className={isActive ? "active" : ""}>
                    <button
                      type="button"
                      role="tab"
                      aria-selected={isActive}
                      aria-current={isActive ? "page" : undefined}
                      className={`crud-form-tab-btn ${isActive ? "is-active" : ""}`}
                      onClick={() => onTabChange && onTabChange(tab.tabId)}
                    >
                      {tab.iconClass ? (
                        <i className={`${tab.iconClass} crud-form-tab-icon`} aria-hidden="true"></i>
                      ) : null}
                      <span>{tab.label}</span>
                    </button>
                  </li>
                );
              })}
            </ul>
          </div>
        </div>
      ) : null}

      <div className="tab-content crud-form-tab-content">
        {currentTab.groups
          .filter((group) => Array.isArray(group.fields) && group.fields.length > 0)
          .map((group) => (
            <section key={group.groupId} className="crud-form-tab-group">
              {group.label ? (
                <h4 className="crud-form-tab-group-title">{group.label}</h4>
              ) : null}
              {renderFieldsGrid(group.fields)}
            </section>
          ))}

        {currentTab.fields.length > 0 ? (
          <section className="crud-form-tab-group crud-form-tab-group-ungrouped">
            {renderFieldsGrid(currentTab.fields)}
          </section>
        ) : null}
      </div>
    </div>
  );
};

/**
 * Componente maestro de CRUD que maneja toda la lógica de:
 * - Listado con DataGrid
 * - Paginación y sorting
 * - Modales de añadir/editar con generación automática de formularios
 * - Filtros automáticos
 * - Confirmación de borrado
 * - Mensajes de éxito/error
 *
 * @param {object} service - Servicio con métodos getAll, create, update, delete, updateEnabled
 * @param {array} columns - Definición de columnas para DataGrid, formulario Y filtros automáticos
 *   ESTRUCTURA APLANADA (recomendada - más simple):
 *   [{
 *     key: 'name',                    // REQUERIDO - identificador del campo
 *     label: 'Nombre',                // REQUERIDO - etiqueta a mostrar
 *     sortable: true,                 // ordenable en tabla
 *     render: (row) => row.name,      // OPCIONAL - solo para casos especiales (auto-generado)
 *
 *     // Formulario (todas OPCIONALES):
 *     showInForm: true,               // mostrar en formulario
 *     type: 'text',                   // tipo: 'text'|'textarea'|'number'|'select'|'image'|'email'|'password'
 *     placeholder: 'Enter...',        // placeholder
 *     required: true,                 // validación automática
 *     icon: 'fa-user',                // ícono FontAwesome
 *     fullWidth: true,                // ocupar ancho completo (solo si hay 2+ campos)
 *     rows: 3,                        // para textarea
 *     step: '0.01', min: '0',         // para number
 *     decimals: 2,                    // para formateo automático en grid
 *     options: [{value, label}],      // para select
 *     changeButtonText: '...',        // para image
 *     disabled: (isEditing) => ...,   // deshabilitar condicionalmente
 *
 *     // Filtros (todas OPCIONALES - heredan de formulario):
 *     showInFilter: true,             // mostrar en filtros
 *     filterKey: 'categoryId',        // si backend espera otro nombre
 *     filterSize: 'large',            // 'small'|'medium'|'large' (auto si no se especifica)
 *     filterPlaceholder: 'All...',    // placeholder (hereda si no se especifica)
 *     filterLabel: 'Enabled'          // label personalizada para checkbox
 *   }]
 *
 *   LAYOUT AUTOMÁTICO DEL FORMULARIO:
 *     - 1 campo: ancho completo automático (sin grid)
 *     - 2-6 campos: grid de 2 columnas
 *     - 7+ campos: grid de 3 columnas
 *
 *   ESTRUCTURA ANIDADA (legacy - sigue funcionando):
 *   Puedes seguir usando formConfig{} y filterConfig{} si prefieres
 * @param {string} entityName - Nombre de la entidad (para traducciones)
 * @param {object} icon - Objeto de icono FontAwesome (ej: faArchive de @fortawesome/free-solid-svg-icons)
 * @param {function} renderForm - OPCIONAL: Función personalizada que renderiza el formulario (recibe {formData, onChange, isEditing, renderDefaultForm})
 *                                Si no se proporciona, se genera automáticamente desde columns
 * @param {object} initialFormData - Datos iniciales del formulario (OPCIONAL si se usa formConfig en columns)
 * @param {function} validateForm - OPCIONAL: Función personalizada de validación (recibe formData, devuelve string de error o null)
 *                                  Si no se proporciona, se validan automáticamente los campos required
 * @param {function} renderFilters - OPCIONAL: Función personalizada que renderiza los filtros
 *                                   Si no se proporciona, se generan automáticamente desde columns con filterConfig
 * @param {boolean} hasEnabledToggle - OBSOLETO: Se detecta automáticamente si hay columna 'enabled'
 * @param {function} onBeforeCreate - OPCIONAL: Callback antes de crear (recibe formData, devuelve formData transformado)
 * @param {function} onBeforeUpdate - OPCIONAL: Callback antes de actualizar (recibe formData, devuelve formData transformado)
 * @param {function} onBeforeEdit - OPCIONAL: Callback antes de editar (recibe data, devuelve data transformado)
 * @param {function} onFormDataChange - OPCIONAL: Callback al cambiar datos del formulario
 * @param {function} onFormOpen - OPCIONAL: Callback al abrir el formulario de alta/edición
 *                                (recibe data, { isEditing }). Útil para tomar una foto del
 *                                estado inicial de esa sesión de edición (p.ej. para lógica de
 *                                campos mutuamente excluyentes vía disabled(isEditing, formData)).
 * @param {boolean} canCreate - Si puede añadir registros (default: true)
 * @param {boolean} canEdit - Si puede editar registros (default: true)
 * @param {boolean} canDelete - Si puede borrar registros (default: true)
 * @param {boolean} canFilter - Si puede mostrar filtros (default: true)
 * @param {object} dynamicOptions - OPCIONAL: Objeto con opciones dinámicas para selects (ej: { condominio: [{value: 'id', label: 'nome'}] })
 */
const CrudManager = ({
  service,
  columns: providedColumns,
  entityName,
  icon,
  renderForm,
  initialFormData,
  validateForm,
  renderFilters,
  onBeforeCreate,
  onBeforeUpdate,
  onBeforeEdit,
  onFormDataChange,
  onFormOpen,
  // Parámetros de control de operaciones
  canCreate = true,
  canEdit = true,
  canDelete = true,
  canFilter = true,
  // Componente de formulario custom (opcional)
  customFormComponent = null,
  formLayout = "modal",
  formTabs = null,
  showPageHeader = true,
  onRowDoubleClick = null,
  autoOpenSingleRowOnLoad = false,
  autoOpenContextKey = null,
  autoOpenRowIdField = null,
  autoOpenRowIdResolver = null,
  modalClassName = "",
  initialFilters = {},
  initialSort = null,
  gridRowStyleResolver = null,
  joinRowGroupingDefaults = {},
  renderRowActions = null,
  renderCreateActions = null,
  pagePreferenceKey = null,
  gridPreferenceKey = null,
  filterPreferenceKey = null,
  allowGridConfiguration = true,
  allowFilterConfiguration = true,
  allowSavedFilters = true,
  allowGridReports = true,
  permissionPrefix = null,
  fieldPermissionPrefix = null,
  externalRefreshToken = null,
  // Opciones dinámicas para selects (recibe objeto con arrays de opciones por key)
  dynamicOptions = {},
}) => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const { hasPermission } = useAuth();
  const effectiveFieldPermissionPrefix = fieldPermissionPrefix || permissionPrefix;
  const columns = useMemo(
    () => (providedColumns || [])
      .filter((column) =>
        !column.fieldPermissionControlled ||
        !effectiveFieldPermissionPrefix ||
        hasPermission(`${effectiveFieldPermissionPrefix}.field.${column.key}.view`)
      )
      .map((column) => {
        const translatedColumn = {
          ...column,
          options: translateOptionLabels(column.options, t),
          formConfig: column.formConfig
            ? {
                ...column.formConfig,
                options: translateOptionLabels(column.formConfig.options, t),
              }
            : column.formConfig,
        };
        if (!column.fieldPermissionControlled || !effectiveFieldPermissionPrefix) {
          return translatedColumn;
        }
        const editable = hasPermission(
          `${effectiveFieldPermissionPrefix}.field.${column.key}.edit`,
        );
        // column.disabled may be a function (see disabled(isEditing, formData)
        // in the JSDoc above) - Boolean(fn) is always true, so flattening it
        // here would permanently disable any field with dynamic disabled logic
        // the moment it becomes field-permission-controlled (the default for
        // every generated column). Preserve the function and only short-circuit
        // on missing edit permission.
        const originalDisabled = column.disabled;
        const resolvedDisabled =
          typeof originalDisabled === "function"
            ? (isEditingArg, formDataArg) =>
                !editable || originalDisabled(isEditingArg, formDataArg)
            : Boolean(originalDisabled) || !editable;
        return {
          ...translatedColumn,
          editable: Boolean(column.editable) && editable,
          disabled: resolvedDisabled,
        };
      }),
    [effectiveFieldPermissionPrefix, hasPermission, providedColumns, t],
  );
  const effectiveCanCreate =
    canCreate && (!permissionPrefix || hasPermission(`${permissionPrefix}.create`));
  const effectiveCanEdit =
    canEdit && (!permissionPrefix || hasPermission(`${permissionPrefix}.update`));
  const effectiveCanDelete =
    canDelete && (!permissionPrefix || hasPermission(`${permissionPrefix}.delete`));
  const allowedReportFormats = [
    { format: "XLSX", permission: "export.xlsx" },
    { format: "PDF", permission: "export.pdf" },
    { format: "CSV", permission: "export.csv" },
  ].filter(({ permission }) =>
    !permissionPrefix || hasPermission(`${permissionPrefix}.${permission}`)
  ).map(({ format }) => format);

  // ðŸŽ‰ Detectar automáticamente si hay columna 'enabled'
  const autoHasEnabledToggle = columns.some((col) => col.key === "enabled");

  // Generar initialFormData automáticamente si no se proporciona
  const autoInitialFormData =
    initialFormData || generateInitialFormData(columns);

  // Estados de datos
  const [data, setData] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [loadingOperation, setLoadingOperation] = useState(false);

  // Paginación
  const [currentPage, setCurrentPage] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const effectivePagePreferenceKey = pagePreferenceKey || entityName;
  const effectiveGridPreferenceKey = gridPreferenceKey || entityName;
  const effectiveFilterPreferenceKey = filterPreferenceKey || entityName;
  const [totalElements, setTotalElements] = useState(0);
  const [numberOfElements, setNumberOfElements] = useState(0);

  // Sorting
  const [sortConfig, setSortConfig] = useState({
    key: initialSort?.key || null,
    backendKey: initialSort?.backendKey || null,
    direction: initialSort?.direction || "ascending",
  });
  const initialSortConfig = {
    key: initialSort?.key || null,
    backendKey: initialSort?.backendKey || null,
    direction: initialSort?.direction || "ascending",
  };

  // Filtros
  const [filters, setFilters] = useState(initialFilters || {});
  const [filterStates, setFilterStates] = useState(initialFilters || {}); // Estados locales de filtros para inputs
  const [internalFormDynamicOptions, setInternalFormDynamicOptions] = useState(
    {},
  );
  const [internalFilterDynamicOptions, setInternalFilterDynamicOptions] =
    useState({});
  // Usar dynamicOptions externo si tiene contenido, sino usar estado interno
  const hasExternalOptions =
    dynamicOptions &&
    Object.keys(dynamicOptions).some(
      (key) =>
        Array.isArray(dynamicOptions[key]) && dynamicOptions[key].length > 0,
    );
  const effectiveFormDynamicOptions = hasExternalOptions
    ? dynamicOptions
    : internalFormDynamicOptions;
  const effectiveFilterDynamicOptions = hasExternalOptions
    ? dynamicOptions
    : internalFilterDynamicOptions;
  const effectiveGridDynamicOptions = hasExternalOptions
    ? dynamicOptions
    : { ...internalFilterDynamicOptions, ...internalFormDynamicOptions };
  const columnsWithResolvedOptions = buildColumnsWithResolvedOptions(
    columns,
    effectiveGridDynamicOptions,
  );
  const selectDependentsMap = buildSelectDependencyMap(columns);

  // Formulario
  const [formData, setFormData] = useState(autoInitialFormData);
  const [isEditing, setIsEditing] = useState(false);
  const [editId, setEditId] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isInlineFormVisible, setIsInlineFormVisible] = useState(false);
  const isInlineLayout = formLayout === "inline";
  const showInlineForm = isInlineLayout && isInlineFormVisible;
  const normalizedFormTabs = useMemo(
    () => normalizeFormTabs(formTabs, columns, t),
    [formTabs, columns, t],
  );
  const [activeFormTabId, setActiveFormTabId] = useState(null);
  const [hasAutoOpenedForm, setHasAutoOpenedForm] = useState(false);

  // Mensajes
  const [modalError, setModalError] = useState("");
  const [modalSuccess, setModalSuccess] = useState("");
  const [inlineSuccess, setInlineSuccess] = useState("");
  const [confirmDialogSuccess, setConfirmDialogSuccess] = useState("");
  const [confirmDialogError, setConfirmDialogError] = useState("");
  const [fieldErrors, setFieldErrors] = useState({}); // Errores por campo

  // Confirmación de borrado
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [itemToDelete, setItemToDelete] = useState(null);

  // Estado para el campo de imagen (si existe)
  const [isEditImageState, setEditImageState] = useState(true);

  // Estado para forzar refresh cuando cambia tenant o dataAreaId
  const [refreshKey, setRefreshKey] = useState(0);
  const [gridPreference, setGridPreference] = useState({
    columnOrder: [],
    hiddenColumns: [],
    columnWidths: {},
    // null = sin preferencia explícita del usuario -> usar el valor por
    // defecto de table-config.yml (joinRowGroupingDefaults). true/false una
    // vez el usuario lo ha alternado desde la pantalla.
    groupJoinedRows: null,
  });
  const [filterPreference, setFilterPreference] = useState({
    fieldOrder: [],
    hiddenFields: [],
  });
  const [showGridConfigurator, setShowGridConfigurator] = useState(false);
  const [showFilterConfigurator, setShowFilterConfigurator] = useState(false);
  const [savingGridPreference, setSavingGridPreference] = useState(false);
  const [savingFilterPreference, setSavingFilterPreference] = useState(false);
  const [reportFormat, setReportFormat] = useState(null);
  const [exportingReport, setExportingReport] = useState(false);
  const [savedFilters, setSavedFilters] = useState([]);
  const [defaultSavedFilterId, setDefaultSavedFilterId] = useState(null);
  const savedFilterPreference = useMemo(
    () => ({ defaultFilterId: defaultSavedFilterId, savedFilters }),
    [defaultSavedFilterId, savedFilters],
  );
  const [selectedSavedFilterId, setSelectedSavedFilterId] = useState(null);
  const [savingSavedFilter, setSavingSavedFilter] = useState(false);
  const [userPreferencesReady, setUserPreferencesReady] = useState(false);

  const snapshotGridPreference = (preference) => ({
    columnOrder: [...(preference?.columnOrder || [])],
    hiddenColumns: [...(preference?.hiddenColumns || [])],
    columnWidths: { ...(preference?.columnWidths || {}) },
    groupJoinedRows:
      preference?.groupJoinedRows === undefined ? null : preference.groupJoinedRows,
  });

  const snapshotFilterPreference = (preference) => ({
    fieldOrder: [...(preference?.fieldOrder || [])],
    hiddenFields: [...(preference?.hiddenFields || [])],
  });

  const applySavedLayoutPreferences = (savedFilter) => {
    if (savedFilter?.gridPreference) {
      setGridPreference(snapshotGridPreference(savedFilter.gridPreference));
    }
    if (savedFilter?.filterPreference) {
      setFilterPreference(snapshotFilterPreference(savedFilter.filterPreference));
    }
  };

  const configurableGridColumns = useMemo(
    () => columns.filter((column) =>
      column?.key &&
      column.key !== "enabled" &&
      (column.type ?? column.formConfig?.type) !== "password" &&
      column.sensitive !== true &&
      column.gridConfigurable !== false
    ),
    [columns],
  );

  const configuredGridColumns = useMemo(() => {
    const configurableKeys = new Set(configurableGridColumns.map((column) => column.key));
    const hiddenKeys = new Set(gridPreference.hiddenColumns || []);
    const columnWidths = gridPreference.columnWidths || {};
    const applyStoredColumnWidth = (column) => {
      const width = Math.round(Number(columnWidths[column.key]));
      if (!Number.isFinite(width) || width < 40 || width > 2000) {
        return column;
      }
      return {
        ...column,
        gridWidth: width,
      };
    };
    const hasCustomPreference =
      (gridPreference.columnOrder || []).length > 0 ||
      (gridPreference.hiddenColumns || []).length > 0;
    const byKey = new Map(columnsWithResolvedOptions.map((column) => [column.key, column]));
    const orderedKeys = [
      ...(gridPreference.columnOrder || []).filter((key) => configurableKeys.has(key) && byKey.has(key)),
      ...configurableGridColumns.map((column) => column.key).filter(
        (key) => !(gridPreference.columnOrder || []).includes(key),
      ),
    ];
    const orderedConfigurable = orderedKeys.map((key) => applyStoredColumnWidth({
      ...byKey.get(key),
      showInGrid: hasCustomPreference
        ? !hiddenKeys.has(key)
        : (byKey.get(key)?.showInGrid ?? true),
    }));
    const fixedColumns = columnsWithResolvedOptions.filter(
      (column) => !configurableKeys.has(column.key),
    ).map(applyStoredColumnWidth);
    return [...orderedConfigurable, ...fixedColumns];
  }, [columnsWithResolvedOptions, configurableGridColumns, gridPreference]);

  const configurableFilterFields = useMemo(
    () => columns.filter((column) => {
      if (!column?.key || column.filterConfigurable === false) {
        return false;
      }
      const filterType = column.filterType ?? column.filterConfig?.type ?? column.type ?? "text";
      return filterType !== "image" && filterType !== "password";
    }),
    [columns],
  );

  const configuredFilterColumns = useMemo(() => {
    const configurableKeys = new Set(configurableFilterFields.map((column) => column.key));
    const hiddenKeys = new Set(filterPreference.hiddenFields || []);
    const hasCustomPreference =
      (filterPreference.fieldOrder || []).length > 0 ||
      (filterPreference.hiddenFields || []).length > 0;
    const byKey = new Map(columns.map((column) => [column.key, column]));
    const orderedKeys = [
      ...(filterPreference.fieldOrder || []).filter((key) => configurableKeys.has(key) && byKey.has(key)),
      ...configurableFilterFields.map((column) => column.key).filter(
        (key) => !(filterPreference.fieldOrder || []).includes(key),
      ),
    ];
    const orderedConfigurable = orderedKeys.map((key) => ({
      ...byKey.get(key),
      showInFilter: hasCustomPreference
        ? !hiddenKeys.has(key)
        : (byKey.get(key)?.showInFilter ?? byKey.get(key)?.filterConfig?.show ?? true),
      filterConfig: byKey.get(key)?.filterConfig
        ? {
            ...byKey.get(key).filterConfig,
            show: hasCustomPreference
              ? !hiddenKeys.has(key)
              : (byKey.get(key)?.filterConfig?.show ?? byKey.get(key)?.showInFilter ?? true),
          }
        : byKey.get(key)?.filterConfig,
    }));
    const fixedColumns = columns.filter(
      (column) => !configurableKeys.has(column.key),
    );
    return [...orderedConfigurable, ...fixedColumns];
  }, [columns, configurableFilterFields, filterPreference]);

  // ¿Hay al menos una columna "de línea" (join) visible en el grid o en los
  // filtros ahora mismo? Sólo entonces tiene sentido mostrar el toggle de
  // agrupar filas, porque sólo entonces el backend puede devolver una fila
  // física por cada línea del pedido.
  const hasActiveJoinedColumns = useMemo(() => {
    const isJoinColumn = (column) => Boolean(column?.sourceJoin);
    const gridActive = configuredGridColumns.some(
      (column) => (column.showInGrid ?? true) && isJoinColumn(column),
    );
    const filterActive = configuredFilterColumns.some(
      (column) => (column.showInFilter ?? column.filterConfig?.show) && isJoinColumn(column),
    );
    return gridActive || filterActive;
  }, [configuredGridColumns, configuredFilterColumns]);

  // Valor por defecto declarado en table-config.yml (joins.<key>.frontend.
  // groupDuplicateRows) para cualquiera de los joins de este documento.
  const configDefaultGroupJoinedRows = useMemo(
    () => Object.values(joinRowGroupingDefaults || {}).some(Boolean),
    [joinRowGroupingDefaults],
  );

  // Preferencia efectiva: la elección explícita del usuario (persistida) si
  // existe, si no el valor por defecto de configuración.
  const effectiveGroupJoinedRows =
    gridPreference.groupJoinedRows === null || gridPreference.groupJoinedRows === undefined
      ? configDefaultGroupJoinedRows
      : gridPreference.groupJoinedRows;

  const toggleGroupJoinedRows = async () => {
    const nextValue = !effectiveGroupJoinedRows;
    const nextPreference = {
      ...gridPreference,
      groupJoinedRows: nextValue,
    };
    setGridPreference(nextPreference);
    try {
      await UserPreferencesService.rememberGridPreference(
        effectiveGridPreferenceKey,
        nextPreference,
      );
    } catch (error) {
      setError(error?.message || t("gridPreferences.saveError"));
    }
  };

  const hasAvailableFilters = Boolean(renderFilters) || configuredFilterColumns.some(
    (column) => column.showInFilter ?? column.filterConfig?.show,
  );

  const visibleReportColumns = useMemo(
    () => configuredGridColumns
      .filter((column) =>
        (column.showInGrid ?? true) &&
        column.exportable !== false &&
        !String(column.key || "").startsWith("__")
      )
      .map((column) => ({
        ...column,
        translatedLabel:
          typeof column.label === "string" && column.label.includes(".")
            ? t(column.label)
            : column.label || column.key,
      })),
    [configuredGridColumns, t],
  );

  // Same set (real, exportable, non-synthetic columns currently shown in the
  // grid) reused so "save filter as alert" captures exactly what the user
  // sees - field names plus their translated labels - instead of every DTO
  // field under its raw, untranslated name (see SaveFilterAsAlertModal).
  const visibleGridColumnKeys = useMemo(() => visibleReportColumns.map((c) => c.key), [visibleReportColumns]);
  const visibleGridColumnLabels = useMemo(
    () => visibleReportColumns.map((c) => c.translatedLabel),
    [visibleReportColumns],
  );

  const loadPageSizePreference = async ({ force = false } = {}) => {
    try {
      const preferences = await UserPreferencesService.get({ force });
      const preferredPageSize = UserPreferencesService.resolvePageSize(preferences, effectivePagePreferenceKey);
      if (preferredPageSize && preferredPageSize !== pageSize) {
        setPageSize(preferredPageSize);
        setCurrentPage(0);
      }
      setGridPreference(
        UserPreferencesService.resolveGridPreference(preferences, effectiveGridPreferenceKey),
      );
      setFilterPreference(
        UserPreferencesService.resolveFilterPreference(preferences, effectiveFilterPreferenceKey),
      );
      const nextDefaultSavedFilterId = UserPreferencesService.resolveDefaultSavedFilterId(
        preferences,
        effectiveFilterPreferenceKey,
      );
      // Always fetched live from the owner's row (own + shared-with-me filters) - never cached
      // client-side - so an edit/unshare by the owner shows up the next time this loads.
      const nextSavedFilters = effectiveFilterPreferenceKey
        ? await SavedFilterService.list(effectiveFilterPreferenceKey).catch(() => [])
        : [];
      setSavedFilters(nextSavedFilters);
      setDefaultSavedFilterId(nextDefaultSavedFilterId);
      const defaultSavedFilter = nextSavedFilters.find(
        (savedFilter) => savedFilter.id === nextDefaultSavedFilterId,
      );
      if (defaultSavedFilter) {
        setFilters(defaultSavedFilter.filters || {});
        setFilterStates(defaultSavedFilter.filterStates || defaultSavedFilter.filters || {});
        setSortConfig(defaultSavedFilter.sort || initialSortConfig);
        applySavedLayoutPreferences(defaultSavedFilter);
        setSelectedSavedFilterId(defaultSavedFilter.id);
        setCurrentPage(0);
      } else {
        setFilters(initialFilters || {});
        setFilterStates(initialFilters || {});
        setSortConfig(initialSortConfig);
        setSelectedSavedFilterId(null);
      }
    } catch (error) {
      if (process.env.NODE_ENV === 'development') {
        console.warn('[CrudManager] Could not load page size preference:', error);
      }
    } finally {
      setUserPreferencesReady(true);
    }
  };

  const renderGeneratedForm = () =>
    autoRenderForm(
      columns,
      formData,
      handleFormChange,
      isEditing,
      t,
      isEditImageState,
      setEditImageState,
      fieldErrors,
      effectiveFormDynamicOptions,
      normalizedFormTabs,
      activeFormTabId,
      setActiveFormTabId,
    );

  // Escuchar eventos de cambio de tenant y dataArea para auto-refresh
  useEffect(() => {
    const handleTenantChanged = () => {
      if (process.env.NODE_ENV === 'development') {
        console.log('[CrudManager] Tenant changed, triggering refresh');
      }
      // Reset pagination when tenant changes
      setCurrentPage(0);
      setUserPreferencesReady(false);
      UserPreferencesService.clearCache();
      loadPageSizePreference({ force: true });
      // Force refresh
      setRefreshKey((prev) => prev + 1);
    };

    const handleDataAreaChanged = () => {
      if (process.env.NODE_ENV === 'development') {
        console.log('[CrudManager] DataArea changed, triggering refresh');
      }
      // Reset pagination when dataArea changes
      setCurrentPage(0);
      // Force refresh
      setRefreshKey((prev) => prev + 1);
    };

    window.addEventListener('tenantChanged', handleTenantChanged);
    window.addEventListener('dataAreaChanged', handleDataAreaChanged);

    return () => {
      window.removeEventListener('tenantChanged', handleTenantChanged);
      window.removeEventListener('dataAreaChanged', handleDataAreaChanged);
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    setUserPreferencesReady(false);
    loadPageSizePreference();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    effectivePagePreferenceKey,
    effectiveGridPreferenceKey,
    effectiveFilterPreferenceKey,
  ]);

  const saveCurrentFilters = async (description, replaceFilterId = null) => {
    setSavingSavedFilter(true);
    try {
      const payload = {
        description,
        filters: { ...filters },
        filterStates: { ...filterStates },
        sort: { ...sortConfig },
        gridPreference: snapshotGridPreference(gridPreference),
        filterPreference: snapshotFilterPreference(filterPreference),
      };
      const saved = replaceFilterId
        ? await SavedFilterService.update(replaceFilterId, payload)
        : await SavedFilterService.create({ entityKey: effectiveFilterPreferenceKey, ...payload });
      setSavedFilters((current) =>
        replaceFilterId
          ? current.map((existingFilter) => (existingFilter.id === saved.id ? saved : existingFilter))
          : [...current, saved],
      );
      setSelectedSavedFilterId(saved.id);
      return true;
    } catch (error) {
      setError(error?.message || t("savedFilters.saveError"));
      return false;
    } finally {
      setSavingSavedFilter(false);
    }
  };

  const updateSelectedSavedFilter = async () => {
    const selectedFilter = savedFilters.find(
      (savedFilter) => savedFilter.id === selectedSavedFilterId,
    );
    if (!selectedFilter || !selectedFilter.owned) {
      return false;
    }
    return saveCurrentFilters(selectedFilter.description, selectedFilter.id);
  };

  const shareSavedFilter = async (filterId, sharing) => {
    const saved = await SavedFilterService.update(filterId, sharing);
    setSavedFilters((current) =>
      current.map((existingFilter) => (existingFilter.id === filterId ? saved : existingFilter)),
    );
    return saved;
  };

  const applySavedFilter = (savedFilter) => {
    setFilters(savedFilter.filters || {});
    setFilterStates(savedFilter.filterStates || savedFilter.filters || {});
    setSortConfig(savedFilter.sort || initialSortConfig);
    applySavedLayoutPreferences(savedFilter);
    setSelectedSavedFilterId(savedFilter.id);
    setCurrentPage(0);
  };

  const selectSavedFilter = (filterId) => {
    if (!filterId) {
      setSelectedSavedFilterId(null);
      return;
    }
    const savedFilter = savedFilters.find(
      (candidate) => candidate.id === filterId,
    );
    if (savedFilter) {
      applySavedFilter(savedFilter);
    }
  };

  const toggleDefaultSavedFilter = async (filterId) => {
    const nextDefaultId = defaultSavedFilterId === filterId ? null : filterId;
    setSavingSavedFilter(true);
    try {
      await UserPreferencesService.rememberDefaultSavedFilterId(effectiveFilterPreferenceKey, nextDefaultId);
      setDefaultSavedFilterId(nextDefaultId);
    } catch (error) {
      setError(error?.message || t("savedFilters.saveError"));
    } finally {
      setSavingSavedFilter(false);
    }
  };

  const deleteSavedFilter = async (filterId) => {
    setSavingSavedFilter(true);
    try {
      await SavedFilterService.remove(filterId);
      setSavedFilters((current) => current.filter((savedFilter) => savedFilter.id !== filterId));
      if (defaultSavedFilterId === filterId) {
        setDefaultSavedFilterId(null);
        await UserPreferencesService.rememberDefaultSavedFilterId(effectiveFilterPreferenceKey, null).catch(() => {});
      }
      if (selectedSavedFilterId === filterId) {
        setSelectedSavedFilterId(null);
      }
    } catch (error) {
      setError(error?.message || t("savedFilters.saveError"));
    } finally {
      setSavingSavedFilter(false);
    }
  };

  const saveGridPreference = async (nextPreference) => {
    setSavingGridPreference(true);
    const preferenceToSave = {
      ...gridPreference,
      ...nextPreference,
      columnWidths: nextPreference.columnWidths || gridPreference.columnWidths || {},
    };
    try {
      await UserPreferencesService.rememberGridPreference(
        effectiveGridPreferenceKey,
        preferenceToSave,
      );
      setGridPreference(preferenceToSave);
      setShowGridConfigurator(false);
    } catch (error) {
      setError(error?.message || t("gridPreferences.saveError"));
    } finally {
      setSavingGridPreference(false);
    }
  };

  const resetGridPreference = async () => {
    setSavingGridPreference(true);
    try {
      await UserPreferencesService.resetGridPreference(effectiveGridPreferenceKey);
      setGridPreference({
        columnOrder: [],
        hiddenColumns: [],
        columnWidths: {},
        groupJoinedRows: null,
      });
      setShowGridConfigurator(false);
    } catch (error) {
      setError(error?.message || t("gridPreferences.saveError"));
    } finally {
      setSavingGridPreference(false);
    }
  };

  const saveGridColumnWidth = async (columnKey, width) => {
    const normalizedWidth = Math.round(Number(width));
    if (!columnKey || !Number.isFinite(normalizedWidth) || normalizedWidth < 40 || normalizedWidth > 2000) {
      return;
    }
    const nextPreference = {
      ...gridPreference,
      columnOrder: gridPreference.columnOrder || [],
      hiddenColumns: gridPreference.hiddenColumns || [],
      columnWidths: {
        ...(gridPreference.columnWidths || {}),
        [columnKey]: normalizedWidth,
      },
    };
    setGridPreference(nextPreference);
    try {
      await UserPreferencesService.rememberGridPreference(
        effectiveGridPreferenceKey,
        nextPreference,
      );
    } catch (error) {
      setError(error?.message || t("gridPreferences.saveError"));
    }
  };

  const removeHiddenFilterValues = (hiddenFields) => {
    if (!Array.isArray(hiddenFields) || hiddenFields.length === 0) {
      return;
    }
    const hiddenKeys = new Set(hiddenFields);
    const hiddenFilterKeys = new Set();
    configurableFilterFields.forEach((field) => {
      if (hiddenKeys.has(field.key)) {
        hiddenFilterKeys.add(field.filterKey ?? field.filterConfig?.filterKey ?? field.key);
      }
    });

    setFilterStates((current) => {
      const next = { ...current };
      hiddenKeys.forEach((key) => delete next[key]);
      return next;
    });
    setFilters((current) => {
      const next = { ...current };
      hiddenFilterKeys.forEach((key) => delete next[key]);
      return next;
    });
  };

  const saveFilterPreference = async (nextPreference) => {
    setSavingFilterPreference(true);
    try {
      await UserPreferencesService.rememberFilterPreference(
        effectiveFilterPreferenceKey,
        nextPreference,
      );
      setFilterPreference(nextPreference);
      removeHiddenFilterValues(nextPreference.hiddenFields);
      setShowFilterConfigurator(false);
      setCurrentPage(0);
    } catch (error) {
      setError(error?.message || t("filterPreferences.saveError"));
    } finally {
      setSavingFilterPreference(false);
    }
  };

  const resetFilterPreference = async () => {
    setSavingFilterPreference(true);
    try {
      await UserPreferencesService.resetFilterPreference(effectiveFilterPreferenceKey);
      setFilterPreference({ fieldOrder: [], hiddenFields: [] });
      setShowFilterConfigurator(false);
    } catch (error) {
      setError(error?.message || t("filterPreferences.saveError"));
    } finally {
      setSavingFilterPreference(false);
    }
  };

  useEffect(() => {
    if (!normalizedFormTabs.length) {
      if (activeFormTabId !== null) {
        setActiveFormTabId(null);
      }
      return;
    }

    const activeStillExists = normalizedFormTabs.some(
      (tab) => tab.tabId === activeFormTabId,
    );
    if (!activeStillExists) {
      setActiveFormTabId(normalizedFormTabs[0].tabId);
    }
  }, [normalizedFormTabs, activeFormTabId]);

  useEffect(() => {
    setHasAutoOpenedForm(false);
  }, [autoOpenContextKey]);

  // Cargar datos - también se ejecuta cuando cambia refreshKey (tenant/dataArea) o sortConfig o pageSize
  useEffect(() => {
    if (userPreferencesReady) {
      fetchData(currentPage);
    }
  }, [currentPage, filters, refreshKey, externalRefreshToken, sortConfig, pageSize, userPreferencesReady]); // eslint-disable-line react-hooks/exhaustive-deps

  // Cargar opciones dinámicas para selects cuando se abre el formulario
  const loadDynamicOptions = async (context, sourceValues = {}) => {
    const optionsToLoad = {};
    const activeLang = normalizeLanguageCode(
      i18n.resolvedLanguage || i18n.language,
    );
    const enumOptionsByName = {};

    for (const col of columns) {
      const showInForm = col.showInForm ?? col.formConfig?.show;
      const showInFilter = col.showInFilter ?? col.filterConfig?.show;
      const relatedService =
        col.relatedService ?? col.formConfig?.relatedService;
      const explicitFilterType = col.filterType ?? col.filterConfig?.type;
      const inheritedFieldType = col.type ?? col.formConfig?.type;
      const fieldType =
        context === "filter" &&
        !explicitFilterType &&
        inheritedFieldType === "select" &&
        relatedService
          ? "text"
          : context === "filter"
            ? explicitFilterType ?? inheritedFieldType
            : inheritedFieldType;
      const enumName = col.enumName ?? col.formConfig?.enumName;

      const includeInContext =
        context === "form" ? showInForm : showInFilter;

      if (includeInContext && fieldType === "select" && enumName && !relatedService) {
        try {
          const cacheKey = `${enumName}|${activeLang}`;
          if (!Object.prototype.hasOwnProperty.call(enumOptionsByName, cacheKey)) {
            const runtimeOptions = await axBaseEnumsMetaService.getOptions(
              enumName,
              activeLang,
            );
            enumOptionsByName[cacheKey] = runtimeOptions;
          }

          const fetchedOptions = enumOptionsByName[cacheKey];
          const fallbackOptions = resolveSelectOptions(col, {});
          if (Array.isArray(fetchedOptions) && fetchedOptions.length > 0) {
            optionsToLoad[col.key] = mergeRuntimeEnumOptions(
              fetchedOptions,
              fallbackOptions,
            );
          } else {
            optionsToLoad[col.key] = fallbackOptions;
          }
        } catch (error) {
          optionsToLoad[col.key] = resolveSelectOptions(col, {});
        }
        continue;
      }

      if (includeInContext && fieldType === "select" && relatedService) {
        try {
          const lookupFilterResult = buildLookupFilters(col, sourceValues);
          if (!lookupFilterResult.canLoad) {
            optionsToLoad[col.key] = [];
            continue;
          }

          const sortArray = buildLookupSortArray(col);
          const data = await relatedService.getAllList(
            0,
            100,
            sortArray,
            lookupFilterResult.filters,
          );
          const firstItem = Array.isArray(data) && data.length > 0 ? data[0] : null;

          const explicitOptionLabel =
            col.optionLabel ?? col.formConfig?.optionLabel;
          const explicitOptionValue =
            col.optionValue ?? col.formConfig?.optionValue;

          const optionLabel = resolvePreferredOptionField(
            firstItem,
            explicitOptionLabel,
            SELECT_LABEL_CANDIDATES,
          );
          const optionValue = resolvePreferredOptionField(
            firstItem,
            explicitOptionValue,
            SELECT_VALUE_CANDIDATES,
          );

          if (!optionLabel || !optionValue) {
            console.warn(
              `[CrudManager] Unable to resolve select options mapping for '${col.key}'.`,
            );
            optionsToLoad[col.key] = [];
            continue;
          }

          const lookupDisplayFields = normalizeLookupFieldList(
            col.lookupDisplayFields ?? col.formConfig?.lookupDisplayFields,
          );
          const lookupSelectedLabelField =
            col.lookupSelectedLabelField ??
            col.formConfig?.lookupSelectedLabelField;

          // Convertir a formato {value, label}
          optionsToLoad[col.key] = data.map((item) => ({
            value: item[optionValue],
            label: buildLookupOptionLabel(
              item,
              optionLabel,
              lookupDisplayFields,
              lookupSelectedLabelField,
            ),
          }));
        } catch (error) {
          console.error(`Error loading options for ${col.key}:`, error);
          optionsToLoad[col.key] = [];
        }
      }
    }

    if (context === "form") {
      setInternalFormDynamicOptions(optionsToLoad);
    } else {
      setInternalFilterDynamicOptions(optionsToLoad);
    }
  };

  useEffect(() => {
    if (!hasExternalOptions && (isModalOpen || showInlineForm)) {
      loadDynamicOptions("form", formData);
    }
  }, [formData, isModalOpen, showInlineForm, hasExternalOptions, i18n.language, i18n.resolvedLanguage]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!hasExternalOptions) {
      loadDynamicOptions("filter", filterStates);
    }
  }, [filterStates, refreshKey, hasExternalOptions, i18n.language, i18n.resolvedLanguage]); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchData = async (page) => {
    setLoadingOperation(true);
    try {
      // Build sort array for backend in format expected by Spring Boot Pageable
      // Format: [{ field: 'name', direction: 'asc' }]
      const sortField = sortConfig.backendKey || sortConfig.key;
      const sortArray = sortField
        ? [{ field: sortField, direction: sortConfig.direction === "ascending" ? "asc" : "desc" }]
        : [];

      // Exclude empty backend filter values.
      const activeFilters = {};
      if (filters && typeof filters === 'object') {
        Object.entries(filters).forEach(([key, value]) => {
          if (value !== undefined && value !== null && value !== '') {
            activeFilters[key] = value;
          }
        });
      }
      const joinTables = resolveJoinTablesForRequest(sortArray, activeFilters);
      if (joinTables.length > 0) {
        activeFilters._joinTables = joinTables.join(",");
      }

      const result = await service.getAll(page, pageSize, sortArray, activeFilters);

      setData(result.content || []);
      setTotalPages(result.totalPages || 1);
      setTotalElements(result.totalElements || 0);
      setNumberOfElements(result.numberOfElements || 0);
      setIsLoading(false);
      setError(null);
    } catch (error) {
      // No mostrar error si es un error de autenticación (ya redirige al login)
      if (!error.isAuthError) {
        setError(error.message);
      }
      setIsLoading(false);
    } finally {
      setLoadingOperation(false);
    }
  };

  // Paginación
  const handlePageChange = (page) => {
    if (page >= 0 && page < totalPages && page !== currentPage) {
      setCurrentPage(page);
      setIsLoading(true);
    }
  };

  const buildActiveFilters = () => {
    const activeFilters = {};
    if (filters && typeof filters === "object") {
      Object.entries(filters).forEach(([key, value]) => {
        if (value !== undefined && value !== null && value !== "") {
          activeFilters[key] = value;
        }
      });
    }
    const joinTables = resolveJoinTablesForRequest(buildActiveSort(), activeFilters);
    if (joinTables.length > 0) {
      activeFilters._joinTables = joinTables.join(",");
    }
    return activeFilters;
  };

  const buildActiveSort = () => {
    const sortField = sortConfig.backendKey || sortConfig.key;
    return sortField
      ? [{
          field: sortField,
          direction: sortConfig.direction === "ascending" ? "asc" : "desc",
        }]
      : [];
  };

  const resolveJoinTablesForRequest = (sortArray = [], activeFilters = {}) => {
    const joins = new Set();
    const registerTable = (table) => {
      const normalized = String(table || "").trim();
      if (
        normalized &&
        normalized.toLowerCase() !== "salestable" &&
        !normalized.includes(".") &&
        !normalized.includes(" ")
      ) {
        joins.add(normalized);
      }
    };

    configuredGridColumns
      .filter((column) => column.showInGrid ?? true)
      .forEach((column) => registerTable(
        column.sourceJoin ||
        inferJoinTableFromField(column) ||
        column.sourceTable ||
        column.table
      ));

    configuredFilterColumns
      .filter((column) => column.showInFilter ?? column.filterConfig?.show)
      .forEach((column) => registerTable(
        column.sourceJoin ||
        inferJoinTableFromField(column) ||
        column.sourceTable ||
        column.table
      ));

    Object.keys(activeFilters || {}).forEach((key) => {
      if (key.includes(".")) {
        registerTable(key.split(".")[0]);
      }
    });

    (sortArray || []).forEach((sortItem) => {
      const field = sortItem?.field;
      if (field && field.includes(".")) {
        registerTable(field.split(".")[0]);
      }
    });

    return [...joins];
  };

  const fetchAllRowsForReport = async () => {
    const batchSize = 1000;
    const activeFilters = buildActiveFilters();
    const activeSort = buildActiveSort();
    const firstPage = await service.getAll(0, batchSize, activeSort, activeFilters);
    const rows = [...(firstPage?.content || [])];
    const pages = Number(firstPage?.totalPages) || 1;
    if (rows.length > 50000) {
      throw new Error(t("gridReports.tooManyRows"));
    }

    for (let page = 1; page < pages; page += 1) {
      const result = await service.getAll(page, batchSize, activeSort, activeFilters);
      rows.push(...(result?.content || []));
      if (rows.length > 50000) {
        throw new Error(t("gridReports.tooManyRows"));
      }
    }
    return rows;
  };

  const buildReportCriteria = () => {
    const criteria = [];
    Object.entries(filterStates || {}).forEach(([key, value]) => {
      if (value === undefined || value === null || value === "") return;
      const column = columnsWithResolvedOptions.find((candidate) =>
        candidate.key === key ||
        candidate.filterKey === key ||
        candidate.filterConfig?.filterKey === key
      );
      const label = column
        ? (
            typeof column.label === "string" && column.label.includes(".")
              ? t(column.label)
              : column.label || key
          )
        : key;
      const displayValue = column ? resolveExportOptionLabel(column, value) : value;
      criteria.push(`${label}: ${displayValue}`);
    });

    if (sortConfig.key) {
      const sortColumn = columnsWithResolvedOptions.find(
        (column) => column.key === sortConfig.key,
      );
      const sortLabel = sortColumn
        ? (
            typeof sortColumn.label === "string" && sortColumn.label.includes(".")
              ? t(sortColumn.label)
              : sortColumn.label || sortConfig.key
          )
        : sortConfig.key;
      criteria.push(t("gridReports.sortCriteria", {
        column: sortLabel,
        direction: t(
          sortConfig.direction === "ascending"
            ? "gridReports.ascending"
            : "gridReports.descending",
        ),
      }));
    }
    return criteria.join(" | ").slice(0, 2000);
  };

  const exportGridReport = async (anonymizedColumnKeys) => {
    setExportingReport(true);
    setError(null);
    try {
      const rows = await fetchAllRowsForReport();
      const anonymized = new Set(anonymizedColumnKeys);
      const reportColumns = visibleReportColumns.map((column) => ({
        key: column.key,
        label: column.translatedLabel,
        anonymized: anonymized.has(column.key),
      }));
      const reportRows = rows.map((row) => {
        const exportRow = {};
        visibleReportColumns.forEach((column) => {
          exportRow[column.key] = normalizeExportValue(row, column, t);
        });
        return exportRow;
      });
      const translatedTitle = t(`${entityName}.title`);
      await GridReportService.export({
        title: translatedTitle === `${entityName}.title` ? entityName : translatedTitle,
        criteria: buildReportCriteria(),
        fileName: entityName,
        resourceKey: permissionPrefix || `legacy.${entityName}`,
        format: reportFormat,
        columns: reportColumns,
        rows: reportRows,
      });
      setReportFormat(null);
    } catch (reportError) {
      setError(reportError?.message || t("gridReports.error"));
    } finally {
      setExportingReport(false);
    }
  };

  // Cambio de tamaño de página
  const handlePageSizeChange = (newSize) => {
    if (newSize !== pageSize) {
      setPageSize(newSize);
      setCurrentPage(0); // Resetear a primera página al cambiar tamaño
      setIsLoading(true);
      UserPreferencesService.rememberPageSize(effectivePagePreferenceKey, newSize).catch((error) => {
        if (process.env.NODE_ENV === 'development') {
          console.warn('[CrudManager] Could not save page size preference:', error);
        }
      });
    }
  };

  // Sorting - Server-side sorting
  const handleSort = (key) => {
    const sortColumn = columns.find((col) => col?.key === key) || {};
    const backendSortKey = sortColumn.sortKey ?? sortColumn.backendSortKey ?? key;
    let direction = "ascending";
    if (sortConfig.key === key && sortConfig.direction === "ascending") {
      direction = "descending";
    }
    // Update sort config and reset to first page
    // useEffect will trigger fetchData when sortConfig changes
    setSortConfig({ key, backendKey: backendSortKey, direction });
    setCurrentPage(0);
  };

  // No client-side sorting - data comes sorted from backend. Lo que sí hacemos
  // en el cliente, si el usuario o la configuración lo piden, es colapsar las
  // filas físicamente duplicadas por un join uno-a-muchos (ver mergeJoinedRows).
  // Nota: esto agrupa dentro de la página actual; el total de elementos y la
  // paginación siguen contando filas (líneas), no cabeceras distintas, porque
  // el join y el LIMIT/OFFSET se aplican en el backend sobre las filas unidas.
  const sortedData = () => {
    if (!effectiveGroupJoinedRows) {
      return data;
    }
    return mergeJoinedRows(data, columns);
  };

  // CRUD
  const handleCreate = async () => {
    if (loadingOperation) {
      return;
    }
    setModalError("");
    setModalSuccess("");
    setFieldErrors({});

    // Usar validación personalizada o automática
    const validationErrors = validateForm
      ? await Promise.resolve(validateForm(formData, t))
      : autoValidateForm(formData, columns, t, entityName);

    if (validationErrors) {
      // Si es un objeto de errores por campo, usarlo
      if (typeof validationErrors === "object") {
        setFieldErrors(validationErrors);
        setModalError(
          t("validation.pleaseFillRequired") ||
            "Por favor, complete los campos obligatorios",
        );
      } else {
        // Si es un string (validación legacy), mostrarlo como antes
        setModalError(validationErrors);
      }
      return;
    }

    setLoadingOperation(true);
    try {
      // ðŸŽ‰ Construcción automática de FormData si hay imágenes
      let dataToSend = onBeforeCreate
        ? await onBeforeCreate(formData)
        : await buildFormData(formData, columns, false);

      await service.create(dataToSend);
      setModalSuccess(t(`${entityName}.success.created`));
      await fetchData(currentPage);

      if (isInlineLayout) {
        setInlineSuccess(t(`${entityName}.success.created`));
        setFormState(autoInitialFormData);
      } else {
        closeForm();
      }
    } catch (error) {
      // No mostrar error si es un error de autenticación (ya redirige al login)
      if (!error.isAuthError) {
        const translatedError = translateErrorMessage(error.message, t);
        setModalError(translatedError || t("common.errorOccurred"));
      }
    } finally {
      setLoadingOperation(false);
    }
  };
  const hasUsableRowId = (rowId) =>
    rowId !== undefined && rowId !== null && rowId !== "";

  const getFieldCandidates = (fieldName) => {
    if (!fieldName || typeof fieldName !== "string") {
      return [];
    }

    const lower = fieldName.toLowerCase();
    const upper = fieldName.toUpperCase();
    const firstUpper = fieldName.charAt(0).toUpperCase() + fieldName.slice(1);
    const firstLower = fieldName.charAt(0).toLowerCase() + fieldName.slice(1);
    const restLower =
      fieldName.length > 1
        ? fieldName.charAt(0).toLowerCase() + fieldName.slice(1).toLowerCase()
        : firstLower;

    return [...new Set([fieldName, lower, upper, firstUpper, firstLower, restLower])];
  };

  const getFieldValueLoose = (source, fieldName) => {
    if (!source || typeof source !== "object" || !fieldName) {
      return undefined;
    }

    const candidates = getFieldCandidates(fieldName);
    for (const candidate of candidates) {
      if (Object.prototype.hasOwnProperty.call(source, candidate)) {
        const value = source[candidate];
        if (value !== undefined && value !== null && value !== "") {
          return value;
        }
      }
    }

    return undefined;
  };

  const normalizeCompositeRowId = (item, rowId) => {
    if (!rowId || typeof rowId !== "object" || Array.isArray(rowId)) {
      return rowId;
    }

    const normalizedId = { ...rowId };
    let hasAtLeastOneValue = false;

    Object.keys(normalizedId).forEach((field) => {
      let value = normalizedId[field];
      if (value === undefined || value === null || value === "") {
        value = getFieldValueLoose(item, field);
      }
      if (value === undefined || value === null || value === "") {
        value = getFieldValueLoose(item?.id, field);
      }
      normalizedId[field] = value;
      if (value !== undefined && value !== null && value !== "") {
        hasAtLeastOneValue = true;
      }
    });

    return hasAtLeastOneValue ? normalizedId : rowId;
  };

  const resolveRowId = (item) => {
    if (!item || typeof item !== "object") {
      return null;
    }

    if (typeof autoOpenRowIdResolver === "function") {
      const resolved = autoOpenRowIdResolver(item);
      if (hasUsableRowId(resolved)) {
        return resolved;
      }
    }

    if (
      autoOpenRowIdField &&
      Object.prototype.hasOwnProperty.call(item, autoOpenRowIdField)
    ) {
      const byField = item[autoOpenRowIdField];
      if (hasUsableRowId(byField)) {
        return byField;
      }
    }

    if (hasUsableRowId(item.id)) {
      return normalizeCompositeRowId(item, item.id);
    }

    return null;
  };

  const buildEditDataFromItem = (item, resolvedId) => {
    if (onBeforeEdit) {
      return onBeforeEdit(item);
    }

    const editData = {};
    columns.forEach((col) => {
      const showInForm = col.showInForm ?? col.formConfig?.show;
      if (!showInForm) {
        return;
      }

      const fieldType = col.type ?? col.formConfig?.type;
      if (fieldType === "select") {
        const backendKey = col.backendKey ?? col.key;
        editData[col.key] =
          item[col.key]?.id ?? item[col.key] ?? item[backendKey] ?? "";
      } else {
        editData[col.key] = normalizeDateValueForForm(fieldType, item[col.key]);
      }
    });

    editData.id = resolvedId;
    editData.enabled = item.enabled;
    return editData;
  };

  const openItemForEdit = (item) => {
    const resolvedId = resolveRowId(item);
    if (!hasUsableRowId(resolvedId)) {
      return false;
    }

    if (customFormComponent) {
      navigate(`/${entityName}-edit?id=${resolvedId}`);
      return true;
    }

    const editData = buildEditDataFromItem(item, resolvedId);
    openEditForm(editData, resolvedId);
    return true;
  };
  const handleEdit = (item) => {
    openItemForEdit(item);
  };

  const handleUpdate = async () => {
    if (loadingOperation) {
      return;
    }
    setModalError("");
    setModalSuccess("");
    setFieldErrors({});

    // Usar validación personalizada o automática
    const validationErrors = validateForm
      ? await Promise.resolve(validateForm(formData, t))
      : autoValidateForm(formData, columns, t, entityName);

    if (validationErrors) {
      // Si es un objeto de errores por campo, usarlo
      if (typeof validationErrors === "object") {
        setFieldErrors(validationErrors);
        setModalError(
          t("validation.pleaseFillRequired") ||
            "Por favor, complete los campos obligatorios",
        );
      } else {
        // Si es un string (validación legacy), mostrarlo como antes
        setModalError(validationErrors);
      }
      return;
    }

    setLoadingOperation(true);
    try {
      // ðŸŽ‰ Construcción automática de FormData si hay imágenes
      let dataToSend = onBeforeUpdate
        ? await onBeforeUpdate(formData, editId)
        : await buildFormData(formData, columns, true);

      await service.update(editId, dataToSend);
      setModalSuccess(t(`${entityName}.success.updated`));
      await fetchData(currentPage);

      if (isInlineLayout) {
        setInlineSuccess(t(`${entityName}.success.updated`));
      } else {
        closeForm();
      }
    } catch (error) {
      // No mostrar error si es un error de autenticación (ya redirige al login)
      if (!error.isAuthError) {
        const translatedError = translateErrorMessage(error.message, t);
        setModalError(translatedError || t("common.errorOccurred"));
      }
    } finally {
      setLoadingOperation(false);
    }
  };

  const handleDelete = (item) => {
    setItemToDelete(item.id);
    setConfirmDialogSuccess("");
    setConfirmDialogError("");
    setShowConfirmDialog(true);
  };

  const closeConfirmDialog = () => {
    setShowConfirmDialog(false);
    setItemToDelete(null);
    setConfirmDialogSuccess("");
    setConfirmDialogError("");
  };

  const confirmDelete = async () => {
    if (itemToDelete) {
      setLoadingOperation(true);
      try {
        const response = await service.delete(itemToDelete);
        const successMessage = t(`${entityName}.success.deleted`);

        // Si el servicio devuelve un objeto con status false, mostrar mensaje elegante
        if (
          response &&
          typeof response === "object" &&
          response.status === false
        ) {
          const translatedDeleteError = t(`${entityName}.errors.deleteFailed`, {
            defaultValue: t("common.deleteFailed", {
              defaultValue: t("common.errorOccurred", {
                defaultValue: "No fue posible eliminar este registro.",
              }),
            }),
          });

          setConfirmDialogSuccess("");
          setConfirmDialogError(translatedDeleteError);
        } else {
          setConfirmDialogError("");

          // Actualizar grid inmediatamente en segundo plano
          await fetchData(currentPage);

          // Mostrar confirmación en la modal y permitir que el usuario cierre manualmente
          setConfirmDialogSuccess(successMessage);
          setItemToDelete(null);
        }
      } catch (error) {
        // No mostrar error si es un error de autenticación (ya redirige al login)
        if (!error.isAuthError) {
          const translatedDeleteError = t(`${entityName}.errors.deleteFailed`, {
            defaultValue: t("common.deleteFailed", {
              defaultValue: t("common.errorOccurred", {
                defaultValue: "No fue posible eliminar este registro.",
              }),
            }),
          });

          setConfirmDialogSuccess("");
          setConfirmDialogError(translatedDeleteError);
        }
      } finally {
        setLoadingOperation(false);
      }
    }
  };

  const handleEnabledChange = async (id, newEnabled) => {
    setLoadingOperation(true);
    try {
      const updatedData = { enabled: newEnabled };
      await service.updateEnabled(id, updatedData);
      await fetchData(currentPage);
      setError(null);
    } catch (error) {
      // No mostrar error si es un error de autenticación (ya redirige al login)
      if (!error.isAuthError) {
        setError(error.message);
      }
    } finally {
      setLoadingOperation(false);
    }
  };

  const handleFormChange = (updatedData) => {
    const changedKeys = Object.keys(updatedData).filter(
      (key) => formData[key] !== updatedData[key],
    );
    if (changedKeys.length === 0) {
      setFormData(updatedData);
      return;
    }

    const fieldsToReset = new Set();
    changedKeys.forEach((changedKey) => {
      collectDependentFields(selectDependentsMap, changedKey).forEach((field) =>
        fieldsToReset.add(field),
      );
    });

    const nextFormData = { ...updatedData };
    fieldsToReset.forEach((field) => {
      nextFormData[field] = "";
    });
    setFormData(nextFormData);

    if (typeof onFormDataChange === "function") {
      const applyFeedback = (feedback) => {
        if (!feedback || typeof feedback !== "object") {
          return;
        }
        if (Object.prototype.hasOwnProperty.call(feedback, "fieldErrors")) {
          setFieldErrors(feedback.fieldErrors || {});
        }
        if (Object.prototype.hasOwnProperty.call(feedback, "modalError")) {
          setModalError(feedback.modalError || "");
        }
        if (
          feedback.formDataPatch &&
          typeof feedback.formDataPatch === "object" &&
          !Array.isArray(feedback.formDataPatch)
        ) {
          setFormData((current) => ({ ...current, ...feedback.formDataPatch }));
        }
      };

      const feedbackResult = onFormDataChange(nextFormData, {
        isEditing,
        columns,
        t,
      });

      if (feedbackResult && typeof feedbackResult.then === "function") {
        feedbackResult.then(applyFeedback).catch(() => {});
      } else {
        applyFeedback(feedbackResult);
      }
    }
  };

  const handleFilterChange = (newFilters) => {
    setFilters(newFilters);
    setFilterStates(newFilters);
    setCurrentPage(0);
  };

  // Manejador de cambio de filtros automáticos
  const handleAutoFilterChange = (stateKey, filterKey, value) => {
    const dependentFields = collectDependentFields(selectDependentsMap, stateKey);

    setFilterStates((prev) => {
      const nextState = { ...prev, [stateKey]: value };
      dependentFields.forEach((field) => {
        nextState[field] = "";
      });
      return nextState;
    });

    setFilters((prev) => {
      const nextFilters = { ...prev, [filterKey]: value };
      dependentFields.forEach((field) => {
        nextFilters[field] = "";
      });
      return nextFilters;
    });
    setCurrentPage(0);
  };

  const setFormState = (data) => {
    setFormData(data);
    setModalError("");
    setModalSuccess("");
    setFieldErrors({});
    setEditImageState(true);
  };

  const openCreateForm = () => {
    if (customFormComponent) {
      navigate(`/${entityName}-add`);
      return;
    }

    setIsEditing(false);
    setEditId(null);
    setInlineSuccess("");
    setActiveFormTabId(normalizedFormTabs[0]?.tabId || null);
    setFormState(autoInitialFormData);

    if (typeof onFormOpen === "function") {
      onFormOpen(autoInitialFormData, { isEditing: false });
    }

    if (isInlineLayout) {
      setIsInlineFormVisible(true);
    } else {
      setIsModalOpen(true);
    }

    // Cargar opciones dinámicas para selects
    loadDynamicOptions("form", autoInitialFormData);
  };

  const openEditForm = (data, id) => {
    setIsEditing(true);
    setEditId(id);
    setInlineSuccess("");
    setActiveFormTabId(normalizedFormTabs[0]?.tabId || null);
    setFormState(data);

    if (typeof onFormOpen === "function") {
      onFormOpen(data, { isEditing: true });
    }

    if (isInlineLayout) {
      setIsInlineFormVisible(true);
    } else {
      setIsModalOpen(true);
    }

    // Cargar opciones dinámicas para selects
    loadDynamicOptions("form", data);
  };

  useEffect(() => {
    if (!autoOpenSingleRowOnLoad || !effectiveCanEdit) {
      return;
    }

    if (isInlineLayout ? showInlineForm : isModalOpen) {
      return;
    }

    if (hasAutoOpenedForm) {
      return;
    }

    if (!Array.isArray(data) || data.length !== 1) {
      return;
    }

    const row = data[0];
    const didOpen = openItemForEdit(row);
    if (!didOpen) {
      return;
    }

    setHasAutoOpenedForm(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    autoOpenSingleRowOnLoad,
    effectiveCanEdit,
    data,
    hasAutoOpenedForm,
    isInlineLayout,
    isModalOpen,
    showInlineForm,
  ]);

  const closeForm = () => {
    if (isInlineLayout) {
      setIsInlineFormVisible(false);
    } else {
      setIsModalOpen(false);
    }
    setIsEditing(false);
    setEditId(null);
    setFormData(autoInitialFormData);
    setModalError("");
    setModalSuccess("");
    setFieldErrors({});
    setEditImageState(true);
    setInlineSuccess("");
    setActiveFormTabId(null);
  };

  // No retornar solo Spinner para evitar remount del componente
  // El Spinner se mostrará como overlay sobre el contenido existente

  return (
    <>
      {(loadingOperation || isLoading) && (
        <Spinner overlay={true} size="large" message={isLoading ? t("common.loading") : t("common.processing")} />
      )}

      {error && (
        <Alert
          type="error"
          message={`${t("common.error")}: ${error}`}
          onClose={() => setError(null)}
          autoClose={5000}
        />
      )}
      {showPageHeader && (
        <PageHeader
          title={
            showInlineForm
              ? t(isEditing ? `${entityName}.edit` : `${entityName}.add`)
              : t(`${entityName}.title`)
          }
          subtitle={t(`${entityName}.subtitle`)}
          icon={icon}
        >
          {showInlineForm && (
            <button type="button" className="btn btn-primary" onClick={closeForm}>
              <i className="fa fa-arrow-left"></i>
              <span>{t("common.goBack")}</span>
            </button>
          )}
        </PageHeader>
      )}


      <section className="panel">
        <div className="panel-body">
          {/* Botón añadir - solo si canCreate es true */}
          {effectiveCanCreate && !showInlineForm && (
            <div className="button-container text-right">
              {renderCreateActions}
              <button className="btn btn-primary" onClick={openCreateForm}>
                {t(`${entityName}.add`)}
              </button>
            </div>
          )}

          {/* Filtros personalizados o automáticos - solo si canFilter es true */}
          {canFilter &&
            !showInlineForm &&
            (renderFilters
              ? renderFilters({ filters, onFilterChange: handleFilterChange })
              : autoRenderFilters(
                  configuredFilterColumns,
                  filterStates,
                  handleAutoFilterChange,
                  t,
                  effectiveFilterDynamicOptions,
                ))}

          {canFilter && hasAvailableFilters && allowSavedFilters && !showInlineForm && (
            <SavedFiltersToolbar
              preference={savedFilterPreference}
              selectedFilterId={selectedSavedFilterId}
              onSelect={selectSavedFilter}
              onApply={applySavedFilter}
              onSave={saveCurrentFilters}
              onUpdate={updateSelectedSavedFilter}
              onToggleDefault={toggleDefaultSavedFilter}
              onDelete={deleteSavedFilter}
              onShare={shareSavedFilter}
              saving={savingSavedFilter}
              entityName={entityName}
              filters={filters}
              visibleColumns={visibleGridColumnKeys}
              visibleColumnLabels={visibleGridColumnLabels}
            />
          )}

          {/* DataGrid */}
          {!showInlineForm && (
            <DataGrid
              columns={configuredGridColumns}
              data={sortedData()}
              sortConfig={sortConfig}
              onSort={handleSort}
              onRowDoubleClick={onRowDoubleClick}
              onEdit={effectiveCanEdit ? handleEdit : null}
              onDelete={effectiveCanDelete ? handleDelete : null}
              renderRowActions={renderRowActions}
              hasEnabledToggle={autoHasEnabledToggle}
              onEnabledChange={handleEnabledChange}
              pagination={{
                currentPage,
                totalPages,
                totalElements,
                numberOfElements,
                pageSize,
              }}
              onPageChange={handlePageChange}
              onPageSizeChange={handlePageSizeChange}
              showTopPagination={true}
              rowStyleResolver={gridRowStyleResolver}
              onColumnResize={saveGridColumnWidth}
              topPaginationActions={
                (allowGridReports || allowFilterConfiguration || allowGridConfiguration) ? (
                  <div className="grid-toolbar-actions">
                    {allowGridReports && visibleReportColumns.length > 0 && allowedReportFormats.length > 0 && (
                      <GridReportExportMenu
                        onSelect={setReportFormat}
                        disabled={loadingOperation || exportingReport}
                        allowedFormats={allowedReportFormats}
                      />
                    )}
                    {!renderFilters && allowFilterConfiguration && configurableFilterFields.length > 0 && (
                      <button
                        type="button"
                        className="btn btn-default grid-columns-button"
                        onClick={() => setShowFilterConfigurator(true)}
                        title={t("filterPreferences.title")}
                      >
                        <i className="fa fa-filter"></i>
                        <span>{t("filterPreferences.configure")}</span>
                      </button>
                    )}
                    {allowGridConfiguration && configurableGridColumns.length > 0 && (
                      <button
                        type="button"
                        className="btn btn-default grid-columns-button"
                        onClick={() => setShowGridConfigurator(true)}
                        title={t("gridPreferences.title")}
                      >
                        <i className="fa fa-columns"></i>
                        <span>{t("gridPreferences.configure")}</span>
                      </button>
                    )}
                    {hasActiveJoinedColumns && (
                      <button
                        type="button"
                        className={`btn btn-default grid-columns-button${effectiveGroupJoinedRows ? " active" : ""}`}
                        onClick={toggleGroupJoinedRows}
                        title={
                          effectiveGroupJoinedRows
                            ? t("gridPreferences.groupJoinedRowsOnHint")
                            : t("gridPreferences.groupJoinedRowsOffHint")
                        }
                        aria-pressed={effectiveGroupJoinedRows}
                      >
                        <i className={`fa ${effectiveGroupJoinedRows ? "fa-object-group" : "fa-object-ungroup"}`}></i>
                        <span>{t("gridPreferences.groupJoinedRows")}</span>
                      </button>
                    )}
                  </div>
                ) : null
              }
            />
          )}

          {showInlineForm && (
            <>
              <Alert
                type="error"
                message={modalError}
                onClose={() => setModalError("")}
                autoClose={3000}
              />
              {inlineSuccess && (
                <Alert
                  type="success"
                  message={inlineSuccess}
                  onClose={() => setInlineSuccess("")}
                  autoClose={3000}
                />
              )}

              {renderForm
                ? renderForm({
                    formData,
                    onChange: handleFormChange,
                    isEditing,
                    renderDefaultForm: renderGeneratedForm,
                  })
                : renderGeneratedForm()}
            </>
          )}
        </div>

        {showInlineForm && (
          <div
            className="panel-footer"
            style={{ textAlign: "right", padding: "15px" }}
          >
            <button
              type="button"
              className="btn btn-default"
              onClick={closeForm}
              disabled={loadingOperation}
              style={{ marginRight: "10px" }}
            >
              {t("common.cancel")}
            </button>
            <button
              type="button"
              className="btn btn-primary"
              onClick={isEditing ? handleUpdate : handleCreate}
              disabled={loadingOperation}
            >
              {t("common.save")}
            </button>
          </div>
        )}
      </section>

      {/* Modal de añadir/editar */}
      {!isInlineLayout && (
        <Modal
          isOpen={isModalOpen}
          onClose={closeForm}
          title={isEditing ? t(`${entityName}.edit`) : t(`${entityName}.add`)}
          icon={icon}
          className={modalClassName}
        >
          <Alert
            type="error"
            message={modalError}
            onClose={() => setModalError("")}
          />
          <Alert
            type="success"
            message={modalSuccess}
            onClose={() => setModalSuccess("")}
            autoClose={3000}
          />

          <form onSubmit={(e) => e.preventDefault()}>
            {/* Formulario personalizado o generado automáticamente */}
            {renderForm
              ? renderForm({
                  formData,
                  onChange: handleFormChange,
                  isEditing,
                  renderDefaultForm: renderGeneratedForm,
                })
              : renderGeneratedForm()}

            {/* Footer con botones */}
            <div className="modal-footer">
              <button
                type="button"
                className="btn btn-secondary"
                onClick={closeForm}
                disabled={loadingOperation}
              >
                <i className="fa fa-times"></i> {t("common.cancel")}
              </button>
              <button
                type="button"
                className="btn btn-primary"
                onClick={isEditing ? handleUpdate : handleCreate}
                disabled={loadingOperation}
              >
                <i className="fa fa-check"></i>{" "}
                {isEditing ? t("common.update") : t("common.create")}
              </button>
            </div>
          </form>
        </Modal>
      )}

      {/* Modal de confirmación para borrar */}
      <ConfirmDialog
        isOpen={showConfirmDialog}
        onClose={closeConfirmDialog}
        onConfirm={confirmDelete}
        title={t(`${entityName}.deleteTitle`)}
        message={t(`${entityName}.deleteConfirm`)}
        confirmText={t("common.delete")}
        cancelText={t("common.cancel")}
        icon={icon}
        type="danger"
        successMessage={confirmDialogSuccess}
        errorMessage={confirmDialogError}
        onSuccessClose={() => {
          setConfirmDialogSuccess("");
          // No cerrar la modal automáticamente para que usuario confirme manualmente
        }}
        onErrorClose={() => setConfirmDialogError("")}
        isProcessing={loadingOperation}
      />

      <GridColumnConfigurator
        isOpen={showGridConfigurator}
        onClose={() => setShowGridConfigurator(false)}
        columns={configurableGridColumns}
        preference={gridPreference}
        onSave={saveGridPreference}
        onReset={resetGridPreference}
        saving={savingGridPreference}
      />

      <GridFilterConfigurator
        isOpen={showFilterConfigurator}
        onClose={() => setShowFilterConfigurator(false)}
        fields={configurableFilterFields}
        preference={filterPreference}
        onSave={saveFilterPreference}
        onReset={resetFilterPreference}
        saving={savingFilterPreference}
      />

      <GridReportExportModal
        isOpen={Boolean(reportFormat)}
        onClose={() => setReportFormat(null)}
        columns={visibleReportColumns.map((column) => ({
          key: column.key,
          label: column.translatedLabel,
        }))}
        format={reportFormat}
        onExport={exportGridReport}
        exporting={exportingReport}
      />
    </>
  );
};

export default CrudManager;




